import { communityHues } from '@/styles/lifecraft'
/**
 * LifeCraft4U: real content, grounded in the client strategy docs & brand board.
 * NOTE ON PLACEHOLDERS: LifeCrafter names/photos and member counts are illustrative
 * (client has not supplied real hosts yet). Counts are rounded & soft. See README.
 * Codenames (Cosmos/Rhythm/Zen/Zoin/Forever Fit/Lenscraft/The Stage) are from the docs.
 */

export const brand = {
  name: 'LifeCraft4U',
  tagline: 'STEP into MORE',
  creed: 'Respect over reach. Presence over performance.',
  seedTaglines: [
    'Connect Online. Meet Offline. Live Fully.',
    'Not a rewind. A conscious restart.',
    'Where your interests become a community.',
  ],
}

export type NavLink = { label: string; href: string }
export const nav: NavLink[] = [
  { label: 'Communities', href: '#communities' },
  { label: 'How it works', href: '#how' },
  { label: 'LifeCrafters', href: '#lifecrafters' },
  { label: 'Stories', href: '#stories' },
  { label: 'About', href: '#about' },
]

export type Accent = 'violet' | 'coral'
export type Community = {
  name: string // codename
  topic: string
  tagline: string
  blurb: string
  members: string
  image: string // /img/<name>.jpg - Unsplash placeholder, see IMAGE_CREDITS.md
  focus?: string // object-position for the card image crop
  accent: Accent
  paths: string[]
  comingSoon?: boolean
}

export const communities: Community[] = [
  {
    name: 'Cosmos',
    topic: 'Astrology',
    tagline: 'Written in the stars.',
    blurb: 'The sky changes every day, and so do you. A daily reason to pause, read, and reflect. No streak required.',
    members: '12k',
    image: '/img/gen/card-cosmos.jpg',
    accent: 'violet',
    paths: ['Daily reading', 'Vedic & KP', 'Tarot', 'Numerology'],
  },
  {
    name: 'Rhythm',
    topic: 'Music',
    tagline: 'Practice, heard.',
    blurb: 'Record your riyaaz, get a real video reply from your LifeCrafter. Solitary practice becomes a relationship.',
    members: '9.8k',
    image: '/img/gen/card-rhythm.jpg',
    accent: 'coral',
    paths: ['Vocals', 'Guitar', 'Tabla & percussion', 'Songwriting'],
  },
  {
    name: 'Zen',
    topic: 'Yoga',
    tagline: 'Breath, posture, stillness.',
    blurb: 'A calm daily practice guided by verified teachers: movement for every body, at your pace.',
    members: '7.3k',
    image: '/img/gen/card-zen-v2.jpg',
    accent: 'violet',
    paths: ['Hatha', 'Breathwork', 'Restorative', 'Mobility'],
  },
  {
    name: 'Zoin',
    topic: 'Meditation',
    tagline: 'Sit. Together.',
    blurb: 'Guided sittings and one shared communal moment a day. Presence you can feel, never a score to chase.',
    members: '6.1k',
    image: '/img/zoin.jpg',
    accent: 'violet',
    paths: ['Guided sits', 'Vipassana', 'Sound baths', 'Sleep'],
  },
  {
    name: 'Forever Fit',
    topic: 'Fitness',
    tagline: 'Strong, for life.',
    blurb: 'Movement and strength built around how you feel, not a leaderboard. Presence over performance, always.',
    members: '8.6k',
    image: '/img/forever-fit.jpg',
    accent: 'coral',
    paths: ['Strength', 'Mobility', 'Walking clubs', 'Recovery'],
  },
  {
    name: 'Spirituality',
    topic: 'Seekers & Wisdom',
    tagline: 'Walk your path.',
    blurb: 'A spine of daily practice and a circle of fellow seekers: diverse paths, one shared respect.',
    members: '15k',
    image: '/img/spirituality.jpg',
    accent: 'coral',
    paths: ['Bhakti & kirtan', 'Gita study', 'Seva', 'Silence'],
    comingSoon: true,
  },
  {
    name: 'Dance',
    topic: 'Movement',
    tagline: 'Move to feel.',
    blurb: 'Classical, folk and free movement: joyful expression for every body and every age.',
    members: '',
    image: '/img/gen/portrait-dancer-motion.jpg',
    focus: 'center 20%',
    accent: 'violet',
    paths: ['Bharatanatyam', 'Kathak', 'Contemporary', 'Folk'],
    comingSoon: true,
  },
  {
    name: 'Lenscraft',
    topic: 'Photography',
    tagline: 'See it, again.',
    blurb: 'Photo walks, gentle critique, and a community that helps you notice the world more slowly.',
    members: '4.5k',
    image: '/img/gen/portrait-photographer.jpg',
    accent: 'violet',
    paths: ['Street', 'Nature', 'Portraits', 'Editing'],
  },
  {
    name: 'The Stage',
    topic: 'Theatre & Voice',
    tagline: 'Find your voice.',
    blurb: 'Read-throughs, storytelling nights and the courage to be seen, on stage or on a call.',
    members: '3.2k',
    image: '/img/the-stage.jpg',
    accent: 'coral',
    paths: ['Acting', 'Storytelling', 'Public speaking', 'Improv'],
  },
]

export type IconName =
  | 'compass' | 'users' | 'calendar' | 'sparkle' | 'shield' | 'heart'
  | 'sun' | 'mic' | 'leaf' | 'star' | 'orbit' | 'lock' | 'mapPin' | 'whatsapp'

export type StepAudience = 'explorer' | 'lifecrafter'
export type Step = { n: string; title: string; body: string; icon: IconName; audience: StepAudience }
export const howItWorks: Step[] = [
  {
    n: '01',
    audience: 'explorer',
    icon: 'compass',
    title: 'Find your community',
    body: 'Communities built around real interests: astrology, music, yoga and more. Wander in, meet the people, pick the one that feels like home.',
  },
  {
    n: '02',
    audience: 'explorer',
    icon: 'users',
    title: 'Show up & take part',
    body: 'Join a community to take part in discussions, comment on posts and share your own photos and videos. Daily programs, live circles and real gatherings, online or near you, at your pace.',
  },
  {
    n: '03',
    audience: 'explorer',
    icon: 'calendar',
    title: 'Go deeper, 1-1',
    body: 'Book private consultations with verified LifeCrafters whenever you want a session that is just for you.',
  },
  {
    n: '01',
    audience: 'lifecrafter',
    icon: 'shield',
    title: 'Apply & get verified',
    body: 'Tell us about your craft. Every LifeCrafter is identity-verified and reviewed before they teach, so the right people can trust you from the first hello.',
  },
  {
    n: '02',
    audience: 'lifecrafter',
    icon: 'calendar',
    title: 'Set up your programs',
    body: 'Create paid programs, events and 1-1 consultation windows in minutes. Your pricing, your schedule.',
  },
  {
    n: '03',
    audience: 'lifecrafter',
    icon: 'mic',
    title: 'Teach, host, earn',
    body: 'Show up for your people and earn predictably. Payments, RSVPs and payouts are handled. You just hold the room.',
  },
]

/* ---- Daily programs: recurring group and 1-1 sessions ----------------- */
export type Program = { title: string; community: string; when: string; where: string; blurb: string }
export const programs: Program[] = [
  { title: 'Sunrise Yoga', community: 'Zen', when: 'Every day · 6:30 AM', where: 'Live on LifeCraft4U', blurb: 'Twenty gentle minutes to open the day. Drop in any morning.' },
  { title: 'The Daily Sit', community: 'Zoin', when: 'Every day · 8:00 AM', where: 'Live on LifeCraft4U', blurb: 'A guided ten-minute sit, together. One shared moment of quiet.' },
  { title: 'Daily Consultation Window', community: 'Cosmos', when: 'Every evening · 6 to 7 PM', where: '1-1 · pay what you feel', blurb: 'Fifteen unhurried minutes with a verified astrologer.' },
  { title: 'Riyaaz Check-in', community: 'Rhythm', when: 'Every day · 7:30 PM', where: 'Live on LifeCraft4U', blurb: 'Post your practice; your LifeCrafter listens back within the week.' },
  { title: 'Evening Verse', community: 'Spirituality', when: 'Every day · 9:30 PM', where: 'Live on LifeCraft4U', blurb: 'One verse, read slowly, to close the day.' },
  { title: 'The Morning Stretch', community: 'Forever Fit', when: 'Every day · 7:00 AM', where: 'Live on LifeCraft4U', blurb: 'Ten unhurried minutes for backs, knees and good moods.' },
]

export type Value = { title: string; body: string; icon: IconName }
export const memberValue: Value[] = [
  { icon: 'sun', title: 'A daily practice', body: 'A calm, dated “Today” that’s yours: a ritual to return to, never a streak to protect.' },
  { icon: 'calendar', title: 'Live events', body: 'Sessions and gatherings online and near you. A trustworthy “what’s on this month”, easy RSVP.' },
  { icon: 'sparkle', title: 'Real LifeCrafters', body: 'Vetted, human experts who reply to you. Not an algorithm, not a guru on a pedestal.' },
  { icon: 'users', title: 'A circle of peers', body: 'People on the same path, at every age. Real connections and lasting friendships, without the noise.' },
]

export type Crafter = {
  name: string
  craft: string
  community: string
  bio: string
  years: string
  initials: string
  photo: string
  accent: Accent
}
// Illustrative - replace with real, verified hosts + photography before launch.
export const crafters: Crafter[] = [
  { name: 'Anjali Rao', craft: 'Yoga & breathwork', community: 'Zen', bio: 'Eighteen years teaching Hatha and breath. Believes stillness is a skill anyone can build.', years: '18 yrs', initials: 'AR', photo: '/img/portrait-1.jpg', accent: 'violet' },
  { name: 'Vikram Menon', craft: 'Hindustani vocals', community: 'Rhythm', bio: 'Trains returning singers with weekly video replies. “Your voice is already enough.”', years: '22 yrs', initials: 'VM', photo: '/img/portrait-3.jpg', accent: 'coral' },
  { name: 'Leela Nair', craft: 'Vedic astrology', community: 'Cosmos', bio: 'Reads charts as maps, not verdicts. Hosts a calm daily reflection for Cosmos.', years: '15 yrs', initials: 'LN', photo: '/img/portrait-6.jpg', accent: 'violet' },
  { name: 'Suresh Iyer', craft: 'Strength for 50+', community: 'Forever Fit', bio: 'Physiotherapist turned coach. Builds strength around how your body actually feels.', years: '12 yrs', initials: 'SI', photo: '/img/portrait-5.jpg', accent: 'coral' },
]

export type Event = {
  title: string
  community: string
  format: 'Online' | 'In person'
  where: string
  when: string
  blurb: string
}
export const events: Event[] = [
  { title: 'Sunrise Photo Walk', community: 'Lenscraft', format: 'In person', where: 'Delhi', when: 'Sat · 6:00 AM', blurb: 'A slow morning through historic gardens. Bring a camera and water.' },
  { title: 'Full-Moon Sit', community: 'Zoin', format: 'Online', where: 'Live on LifeCraft4U', when: 'Thu · 8:30 PM', blurb: 'A guided communal meditation under the full moon. All levels welcome.' },
  { title: 'Riyaaz Circle: Ragas', community: 'Rhythm', format: 'Online', where: 'Live on LifeCraft4U', when: 'Sun · 11:00 AM', blurb: 'Sing together, get gentle feedback from Vikram and the circle.' },
  { title: 'Gentle Strength Meetup', community: 'Forever Fit', format: 'In person', where: 'Bengaluru', when: 'Sun · 7:00 AM', blurb: 'Mobility and strength for every body. Bring water and a mat.' },
]

export type Story = { quote: string; name: string; age: number; note: string; initials: string; photo: string }
export const stories: Story[] = [
  { quote: 'I rediscovered singing at 54, and found people who actually listen back.', name: 'Meera', age: 54, note: 'Rhythm member', initials: 'M', photo: '/img/portrait-2.jpg' },
  { quote: 'It doesn’t feel like an app. It feels like a room I’m glad to walk into each morning.', name: 'Rajesh', age: 63, note: 'Cosmos & Zen', initials: 'R', photo: '/img/portrait-3.jpg' },
  { quote: 'No points, no ranking. Just a practice that’s mine and a circle that shows up.', name: 'Anita', age: 49, note: 'Zoin member', initials: 'A', photo: '/img/portrait-4.jpg' },
]

export type Faq = { q: string; a: string; icon: IconName }
export const trust: Faq[] = [
  { q: 'Verified LifeCrafters', icon: 'shield', a: 'Every host is identity-verified and reviewed before they can teach. You always know who you’re learning from.' },
  { q: 'Moderated, kind spaces', icon: 'heart', a: 'Real people moderate every community to our values: respect all paths, share with compassion, no spam or self-promotion.' },
  { q: 'No scoreboard', icon: 'leaf', a: 'Zero streaks, points, badges or leaderboards, by design. Presence over performance, always.' },
  { q: 'Free to join', icon: 'star', a: 'Joining LifeCraft4U is free. Some LifeCrafter programs are paid and set by the host. There is never a hard paywall on the community itself.' },
]

export const forHosts = {
  eyebrow: 'For LifeCrafters',
  title: 'Build a community around your craft.',
  body: 'Run paid programs, host events, and earn predictably, without fighting an algorithm. A credible profile, simple tools, payouts handled, and the right people finding you.',
  points: ['A verified host profile', 'Programs & events in minutes', 'Payments & payouts handled', 'Qualified people discovering you'],
  cta: 'Become a LifeCrafter',
}

export const footerNav: { heading: string; links: string[] }[] = [
  { heading: 'Explore', links: ['Communities', 'Events', 'Stories', 'LifeCrafter Program'] },
  { heading: 'Company', links: ['About', 'Careers', 'Press', 'Contact'] },
  { heading: 'Trust', links: ['Community guidelines', 'Verification', 'Privacy', 'Terms'] },
  { heading: 'Support', links: ['Help centre', 'Safety', 'Accessibility', 'FAQ'] },
]

/* ================================================================== */
/*  Cosmos (Astrology): community template page content              */
/*  Astrology-specific, on-brand (no fate/fear, no scoreboard).       */
/* ================================================================== */

export type Sign = { name: string; symbol: string; dates: string; element: 'Fire' | 'Earth' | 'Air' | 'Water'; note: string }
export const zodiac: Sign[] = [
  { name: 'Aries', symbol: '♈︎', dates: 'Mar 21 – Apr 19', element: 'Fire', note: 'Begin the thing you keep postponing.' },
  { name: 'Taurus', symbol: '♉︎', dates: 'Apr 20 – May 20', element: 'Earth', note: 'Slow is a strategy, not a failing.' },
  { name: 'Gemini', symbol: '♊︎', dates: 'May 21 – Jun 20', element: 'Air', note: 'A conversation reopens a door.' },
  { name: 'Cancer', symbol: '♋︎', dates: 'Jun 21 – Jul 22', element: 'Water', note: 'Tend to the people who feel like home.' },
  { name: 'Leo', symbol: '♌︎', dates: 'Jul 23 – Aug 22', element: 'Fire', note: 'Warmth given freely returns doubled.' },
  { name: 'Virgo', symbol: '♍︎', dates: 'Aug 23 – Sep 22', element: 'Earth', note: 'Good enough, finished, beats perfect, someday.' },
  { name: 'Libra', symbol: '♎︎', dates: 'Sep 23 – Oct 22', element: 'Air', note: 'Balance is a verb. Keep adjusting.' },
  { name: 'Scorpio', symbol: '♏︎', dates: 'Oct 23 – Nov 21', element: 'Water', note: 'Let one old thing go, gently.' },
  { name: 'Sagittarius', symbol: '♐︎', dates: 'Nov 22 – Dec 21', element: 'Fire', note: 'Curiosity is calling. Answer it.' },
  { name: 'Capricorn', symbol: '♑︎', dates: 'Dec 22 – Jan 19', element: 'Earth', note: 'Build a little, today, on purpose.' },
  { name: 'Aquarius', symbol: '♒︎', dates: 'Jan 20 – Feb 18', element: 'Air', note: 'Your odd idea is worth saying aloud.' },
  { name: 'Pisces', symbol: '♓︎', dates: 'Feb 19 – Mar 20', element: 'Water', note: 'Trust the quiet intuition. It’s early, not wrong.' },
]

export type CosmosPath = { name: string; body: string; icon: IconName; image?: string }
export type CosmosHost = { name: string; craft: string; years: string; bio: string; photo: string }
export type CosmosEvent = { title: string; format: 'Online' | 'In person'; when: string; where: string; blurb: string; price?: string; subPrice?: string }

export const cosmos = {
  name: 'Cosmos',
  topic: 'Astrology',
  tagline: 'Written in the stars.',
  members: '12,400',
  heroLine: 'The sky changes every day. So do you.',
  intro:
    'Cosmos is a calm daily astrology practice and a circle of people who take the sky seriously but hold it lightly. No fate, no fear. Just a mirror for the season you’re in, and company for the walk. Read the day, sit a circle, ask a real astrologer, and return whenever you like.',
  today: { moon: 'Moon in Pisces', phase: 'Waxing crescent', line: 'A day for quiet intuition and small, kind repairs. Say the soft thing first.' },
  whatYouGet: [
    { icon: 'sun' as IconName, title: 'A daily reading', body: 'A short, thoughtful read on the day’s sky, a reason to pause each morning. Calm and dated, never a streak.' },
    { icon: 'calendar' as IconName, title: 'Live circles', body: 'New-moon intention circles and full-moon readings, hosted live: online and, sometimes, near you.' },
    { icon: 'sparkle' as IconName, title: 'Vetted astrologers', body: 'Real, verified LifeCrafters who read charts as maps, not verdicts. Ask them anything, no per-minute meter.' },
    { icon: 'users' as IconName, title: 'A circle of seekers', body: 'People of all ages, curious and kind. Share a chart, a question, a moment, and be met with respect.' },
  ] as { icon: IconName; title: string; body: string }[],
  paths: [
    { name: 'Vedic', body: 'The classical system: charts, dashas and a long view of your seasons.', icon: 'sun' as IconName, image: '/img/sub/cosmos-vedic.jpg' },
    { name: 'KP', body: 'The precise cousin: exact timings and plain-spoken answers.', icon: 'compass' as IconName, image: '/img/sub/cosmos-kp.jpg' },
    { name: 'Nadi', body: 'Ancient leaf readings, approached with curiosity and care.', icon: 'leaf' as IconName, image: '/img/sub/cosmos-nadi.jpg' },
    { name: 'Lal Kitab', body: 'Folk wisdom and simple remedies, held lightly.', icon: 'heart' as IconName, image: '/img/sub/cosmos-lalkitab.jpg' },
    { name: 'Numerology', body: 'Patterns in dates and names, for the numerically curious.', icon: 'orbit' as IconName, image: '/img/sub/cosmos-numerology.jpg' },
    { name: 'Vastu', body: 'The science of space: homes and workplaces that sit right.', icon: 'mapPin' as IconName, image: '/img/sub/cosmos-vastu.jpg' },
    { name: 'Tarot', body: 'Guidance through symbol and image: a conversation with your intuition.', icon: 'star' as IconName, image: '/img/sub/cosmos-tarot.jpg' },
  ] as CosmosPath[],
  hosts: [
    { name: 'Leela Nair', craft: 'Vedic astrology', years: '15 yrs', bio: 'Reads charts as maps, not verdicts. Hosts the calm daily reflection at the heart of Cosmos.', photo: '/img/portrait-6.jpg' },
    { name: 'Farah Sheikh', craft: 'Tarot & intuition', years: '11 yrs', bio: 'Believes the cards start conversations, not verdicts. Runs the beginner-friendly Tarot circle.', photo: '/img/portrait-4.jpg' },
    { name: 'Devang Joshi', craft: 'KP & transits', years: '19 yrs', bio: 'Retired engineer turned astrologer. Loves the maths of the sky and explaining it plainly.', photo: '/img/portrait-3.jpg' },
  ] as CosmosHost[],
  events: [
    { title: 'New-Moon Intentions Circle', format: 'Online' as const, when: 'Thu · 8:30 PM', where: 'Live on LifeCraft4U', blurb: 'Set the month’s quiet intentions together under the new moon.', price: 'Free for members' },
    { title: 'Full-Moon Reading', format: 'Online' as const, when: 'Sun · 9:00 PM', where: 'Live on LifeCraft4U', blurb: 'A guided read of the full-moon sky, with Leela.', price: '₹499', subPrice: '₹299' },
    { title: 'Your Saturn Return, Unpacked', format: 'In person' as const, when: 'Sat · 5:00 PM', where: 'Mumbai', blurb: 'A warm workshop on the sky’s big rite of passage. Chai included.', price: '₹899', subPrice: '₹599' },
    { title: 'Tarot for the Curious', format: 'Online' as const, when: 'Wed · 7:00 PM', where: 'Live on LifeCraft4U', blurb: 'A gentle first step into the cards, with Farah. No experience needed.', price: '₹399', subPrice: '₹249' },
  ] as CosmosEvent[],
  values: [
    { q: 'Diverse paths, one respect', a: 'Vedic, Western, Tarot, numerology: many maps, all welcome. Disagree kindly.' },
    { q: 'No fate, no fear', a: 'We read the sky as a mirror and an invitation, never a sentence. Nothing here is used to frighten or upsell.' },
    { q: 'Verified astrologers', a: 'Every host is identity-verified and reviewed before they read for anyone.' },
    { q: 'No scoreboard', a: 'No streaks, points or rankings, by design. A daily ritual, not a race.' },
  ],
}

/* ---- Cosmos daily engagement blocks (open as modals) ------------------ */
/* `kind` drives how the modal behaves:
 *   'zodiac-daily' / 'zodiac-yearly' — show a 12-sign picker, then that sign's
 *      reading pulled from the Google Sheet tab named in `sheet`.
 *   'muhurat' — show a Car/Home/Marriage/Pooja filter over the Muhurat tab.
 *   undefined — plain modal that just shows `body`.                        */
export type DailyBlock = {
  key: string
  icon: IconName
  title: string
  teaser: string
  body: string[]
  kind?: 'zodiac-daily' | 'zodiac-yearly' | 'muhurat'
  sheet?: string
}
export const cosmosDaily: DailyBlock[] = [
  { key: 'horoscope', icon: 'sun', title: 'Daily horoscope', teaser: 'Pick your sign for today’s reading', body: [], kind: 'zodiac-daily', sheet: 'DailyHoroscope' },
  { key: 'muhurat', icon: 'compass', title: 'Muhurat finder', teaser: 'Auspicious timing for Car, Home, Marriage, Pooja', body: [], kind: 'muhurat', sheet: 'Muhurat' },
  { key: 'yearly', icon: 'star', title: 'Your 2026', teaser: 'The year ahead, sign by sign', body: [], kind: 'zodiac-yearly', sheet: 'Yearly2026' },
  /* Hidden for now — re-enable when the content/tools are ready:
  { key: 'panchang', icon: 'calendar', title: 'Panchang', teaser: "Today's tithi, nakshatra and hours", body: ['Tithi: Shukla Paksha Tritiya. Nakshatra: Revati.', 'Auspicious window: 10:42 AM to 12:08 PM.', 'The full daily panchang, timed to your city, lives inside Cosmos.'] },
  { key: 'compatibility', icon: 'heart', title: 'Compatibility', teaser: 'Two charts, one conversation', body: ['A gentle look at how two charts sit together.', 'Made for conversations, not verdicts. Nothing here decides anything.', 'Try it with a friend inside the community.'] },
  { key: 'quiz', icon: 'sparkle', title: 'Quick quiz', teaser: 'Five questions, one new thing learned', body: ['A tiny daily quiz on the sky: no scores kept, just the pleasure of knowing.', "Today: which planet rules Thursday?", 'Answers and gentle explanations inside Cosmos.'] },
  */
]

/* ---- Cosmos consultations --------------------------------------------- */
export const cosmosConsult = {
  daily: { title: 'Daily consultation', when: 'Every day · 10:00–11:00 AM & 8:00–9:00 PM', original: '₹1100', price: '₹111', note: 'A focused one-on-one reading in the morning or evening window. Book your slot below.' },
  weekend: { title: 'Weekend consultation', when: 'Sat & Sun · 10:00–11:00 AM & 8:00–9:00 PM', original: '₹1100', price: '₹251', note: 'A longer weekend reading of your chart. Book your slot below.' },
}


/* ---- Alliances & partners marketplace (courses, equipment, retreats…) -- */
export type MarketplaceItem = {
  kind: string
  title: string
  partner: string
  blurb: string
  meta: string
  price?: string
}
export type MarketplaceData = {
  kicker: string
  title: string
  intro: string
  categories: string[]
  items: MarketplaceItem[]
  note: string
}

/* ---- why partner with us (shared across communities) ------------------ */
export const partnerWhy = {
  title: 'Why partner with LifeCraft4U?',
  points: [
    'A verified audience of adults who show up and pay for quality',
    'Co-branded programs and events in real rooms, not ad impressions',
    'Listings, payments and payouts handled by us',
    'A human account manager on the Partner tier',
  ],
  cta: 'Become a partner',
}

/* ---- Community "types" sections (schools / genres), from the boards --- */
export type CommunityTypeItem = {
  name: string
  icon: IconName
  /** Optional banner art (SVG in /img). Renders atop the detail panel. */
  image?: string
  body: string
  tagline: string
  what: string
  a: string[]
  b: string[]
  note: string
}
export type CommunityTypes = {
  kicker: string
  title: string
  intro: string
  labelA: string
  labelB: string
  items: CommunityTypeItem[]
}

/* ---- Community base pages (light-skin templates) --------------------- */
/* A mentor card. The first five fields render the compact card everywhere;
   the optional block below (present on featured mentors) lights up a badge +
   rating on the card and a full "View profile" modal (about, stats, teaching
   philosophy, why-learn). Communities without the rich data keep the plain card. */
export type CommunityMentor = {
  name: string
  craft: string
  years?: string
  photo: string
  /** CSS object-position for the card photo, to fine-tune the crop (e.g. 'center top'). */
  focus?: string
  /** CSS object-position for the profile-modal photo (defaults to centre if unset). */
  modalFocus?: string
  /** CSS scale for both card and modal photos, e.g. 1.1 to zoom in 10%. */
  zoom?: number
  line: string
  badge?: string
  location?: string
  languages?: string
  rating?: number
  reviews?: number
  specialties?: string[]
  stats?: { value: string; label: string }[]
  about?: string[]
  philosophy?: string
  whyLearn?: string[]
}
export type CommunityPeople = {
  mentors: CommunityMentor[]
  mentorNote: string
  categories: { category: string; startsAt: string; people: { name: string; line: string; photo: string }[] }[]
}

/**
 * Placeholder "book a consultation" lanes for cosmos / rhythm / zen: the two
 * bookable LifeCrafter tiers — Premium (base pricing) and Elite (premium
 * pricing) — each with a couple of dummy crafters (icon avatar — no real photo —
 * plus a name and location) until real crafters are assigned. Mentors are a
 * third option ("price on request") but shown in their own section above, not
 * here. Kept static on purpose — the live feed (src/lib/lifecrafters.ts) is
 * built but not wired here yet, so we don't surface unassigned/test crafters.
 */
export type LanePerson = { name: string; location?: string; years?: number; focus?: string; bio?: string; photo?: string; imgPos?: string; modalPos?: string; modalZoom?: number }
export type LifecrafterTierLane = { tier: string; note: string; people: LanePerson[] }
export const lifecrafterTierLanes: LifecrafterTierLane[] = [
  { tier: 'Premium', note: 'Base pricing', people: [{ name: 'Aarav Mehta', location: 'Mumbai', years: 6 }, { name: 'Diya Nair', location: 'Bengaluru', years: 8 }] },
  { tier: 'Elite', note: 'Premium pricing', people: [{ name: 'Kabir Sharma', location: 'New Delhi', years: 12 }, { name: 'Ananya Iyer', location: 'Pune', years: 9 }] },
]

/* ---- Zen: real yoga instructors from the client's onboarding roster --------
   Grouped into their assigned tier (Premium / Elite). Each name is clickable
   and opens a bio pop-up; lanes show three, then "View all". ----------------- */
export const zenInstructorLanes: LifecrafterTierLane[] = [
  {
    tier: 'Premium',
    note: 'Base pricing',
    people: [
      { name: 'Meenu Pal', focus: 'Hatha & Ashtanga · therapeutic', bio: '5 years as a Yoga Instructor plus 2 years specifically as a Yoga Therapist, specialising in Hatha, Ashtanga, and a full range of therapeutic and mindfulness work. Holds both an M.Sc. and B.Sc. in Yoga Science.', photo: '/img/instr/meenu-pal.jpg' },
      { name: 'Deepak Nainwal', focus: 'All-round traditional & therapeutic', bio: '7 years of experience with the broadest specialisation coverage in the pool: every traditional style, all mindfulness disciplines, and all four specialised program areas (Prenatal, Therapeutic, Senior Wellness, Rehabilitation).', photo: '/img/instr/deepak-nainwal.jpg' },
      { name: 'Pawan', focus: 'Hatha, Iyengar & Power · all-round', bio: '6 years of teaching across schools, corporate offices, home visits and studios. Nearly all-round expertise: Hatha, Ashtanga, Vinyasa, Iyengar & Power Yoga, full mindfulness training, and all four specialised areas. Holds an M.Sc. in Yogic Science plus a Certified Yoga Coach credential from NSNIS, Patiala.', photo: '/img/instr/pawan.jpg' },
      { name: 'Raghvendra', focus: 'Hatha & Vinyasa · senior wellness', bio: '4+ years in Hatha & Vinyasa Yoga, with a strong therapeutic and senior-wellness focus. Holds a YCB certification, an MA in Yoga, and a PG Diploma in Yoga Therapy from the School of Yoga, DAVV, one of the more clinically-qualified instructors in the pool.', photo: '/img/instr/raghvendra.jpg' },
      { name: 'Indrani', focus: 'Hatha, Yin & Power · mindfulness', bio: '4.5 years of experience specialising in Hatha, Yin, Ashtanga & Power Yoga, plus a full mindfulness suite and Therapeutic & Senior Wellness Yoga. Holds an M.A. in Yoga & Consciousness from Andhra University.', photo: '/img/instr/indrani.jpg' },
      { name: 'Sangeeta Mandal', focus: 'Hatha & Power · rehabilitation', bio: '5 years of experience specialising in Hatha, Ashtanga & Power Yoga, plus a full mindfulness suite and Senior Wellness, Therapeutic & Rehabilitation Yoga. Holds an M.A. in Yogic Science & Naturopathy.', photo: '/img/instr/sangeeta-mandal.jpg', imgPos: '70% center' },
      { name: 'Nidhi', focus: 'Traditional yoga · prenatal & postnatal', bio: '1 year in Traditional Yoga (Hatha, Ashtanga) plus specialisation in Prenatal & Postnatal Yoga, with a full suite of mindfulness and meditation skills. Certified Wellness Instructor from the Yoga Certification Board (YCB).', photo: '/img/instr/nidhi.jpg' },
      { name: 'Shubhniti', focus: 'Hatha, Yin & Vinyasa · prenatal', bio: '5.5 years of experience in Hatha, Yin, Vinyasa & Power Yoga, with Prenatal and Senior Wellness specialisation. RYT 200 certified through Yoga Alliance.', photo: '/img/instr/shubhniti.jpg' },
    ],
  },
  {
    tier: 'Elite',
    note: 'Premium pricing',
    people: [
      { name: 'Nayan Tomar', focus: 'Hatha · meditation & pranayama', bio: '3.5 years of experience specialising in Hatha Yoga, Meditation & Pranayama, and Prenatal/Postnatal & Therapeutic Yoga. Holds a 200-hr TTC from Bodhi School of Yoga plus an 85-hr Pre/Post-Natal certification, and is registered with the Yoga and Naturopathy Practitioners Association of India.', photo: '/img/instr/nayan-tomar.jpg', imgPos: 'center 30%', modalPos: 'center 45%' },
      { name: 'Vidhi Varshney', focus: 'Yin, Ashtanga & Vinyasa · therapeutic', bio: '3 years across Yin, Ashtanga, Kundalini & Vinyasa styles, with a strong therapeutic and senior-wellness focus backed by training in auricular therapy, pulse diagnosis and pranic healing. Holds an M.Sc. in Yogic Science & Alternative Therapies.', photo: '/img/instr/vidhi-varshney.jpg' },
      { name: 'Sukanta', focus: 'Hatha & Kundalini · senior wellness', bio: '2 years teaching plus 8 years as a competitive yoga participant, specialising in Hatha & Kundalini Yoga, Meditation, and Senior Wellness. YCB Level-3 and PGDYEd certified from Kaivalyadhama.', photo: '/img/instr/sukanta.jpg' },
      { name: 'Shalini Shukla', focus: 'Hatha · mindfulness & therapeutic', bio: '2 years focused on Hatha Yoga, Mindfulness Practices, and Therapeutic Yoga. Holds a PG Diploma in Yoga Training from the Maharishi Dayanand Vocational Training Institute.', photo: '/img/instr/shalini-shukla.jpg', modalPos: 'center 60%' },
      { name: 'Shivani', focus: 'Hatha & Power · therapeutic', bio: '1.5 years specialising in Hatha & Power Yoga, Meditation & Pranayama, and Therapeutic Yoga. Holds a 200-hr TTC from The Yoga Institute, Delhi.', photo: '/img/instr/shivani.jpg', imgPos: 'center 70%' },
    ],
  },
]

/* ---- Rhythm: real music instructors from the client's onboarding pack --------
   Grouped into Premium / Elite, each clickable for a bio pop-up with photo. --- */
export const musicInstructorLanes: LifecrafterTierLane[] = [
  {
    tier: 'Premium',
    note: 'Base pricing',
    people: [
      { name: 'Amaresh Chakraborty', focus: 'Drums · percussion', photo: '/img/instr/amaresh-chakraborty.jpg', imgPos: '20% center', modalPos: '34% 26%', modalZoom: 2.4, bio: 'A distinguished, highly versatile musician, drummer and Octapad player based in New Delhi with over 40 years of dedicated experience across the Indian and global music landscape.' },
      { name: 'Mintal Gazi', focus: 'Guitar · multi-instrument', photo: '/img/instr/mintal-gazi.jpg', imgPos: '20% center', modalPos: '25% 15%', modalZoom: 2.5, bio: 'A seasoned professional musician with over three decades of passion for music, based in Delhi, with versatile expertise across a wide array of string instruments and national and international stages.' },
      { name: 'Piyali Halder', focus: 'Classical, Bengali & Hindi vocals', photo: '/img/instr/piyali-halder.jpg', bio: 'An accomplished Grade A Akashvani and Doordarshan artist based in Delhi, with over 25 years across Classical, Bengali and Hindi genres. Holds an M.A. in Vocal Music from Rabindra Bharati University, Kolkata.' },
      { name: 'Sayan Bose', focus: 'Hindustani classical vocals', photo: '/img/instr/shayan-bose.jpg', bio: 'A Hindustani classical vocalist and performer with 25+ years of performance and classical training, specialising in raga rendition.' },
      { name: 'Arushi Bajpai', focus: 'Hindustani classical vocals', photo: '/img/instr/arushi-bajpai.jpg', bio: 'A versatile Hindustani classical vocalist and instructor with over eight years of experience, trained under Ustaad Ghulam Dastgir Khan Sahab of the Rampur Sahaswan Gharana. Holds a Master’s in Indian Classical Music.' },
      { name: 'Arpit Bhonsle', focus: 'Bollywood & light vocals', photo: '/img/instr/arpit-bhosle.jpg', bio: 'A singer, composer, sound engineer and music producer with 14 years of training in Indian classical music, and vocal expertise across ghazals and Sufi.' },
      { name: 'Srividya Ghatti', focus: 'Carnatic & devotional', photo: '/img/instr/srividya.jpg', bio: 'An accomplished Carnatic classical, devotional and light-music vocalist with teaching experience including Artium Academy, Mumbai, and an M.A. in music.' },
      { name: 'Shweta', focus: 'Ghazal & light classical', photo: '/img/instr/shweta.jpg', bio: 'A dedicated Hindustani classical, ghazal and Sugam Sangeet vocalist with extensive training and national recognition, whose journey began in childhood under her father, Shri Arjun Dev.' },
      { name: 'Vidhya Sonar', focus: 'Hindustani classical vocals', photo: '/img/instr/vidhya-sonar.jpg', bio: 'A trained Indian classical and Sugam Sangeet vocalist and dedicated music educator from Nashik, Maharashtra, whose early love of music grew into a lifelong pursuit.' },
      { name: 'Rahul Mukherjee', focus: 'Hindustani classical vocals', photo: '/img/instr/rahul-mukherjee.jpg', bio: 'A Hindustani classical vocalist and performer specialising in raga performance, trained at Pracheen Kala Kendra.' },
      { name: 'Aindrila Sarkar', focus: 'Bollywood & vocal training', photo: '/img/instr/aindrila-sarkar.jpg', imgPos: '50% 30%', modalPos: '50% 24%', modalZoom: 1.4, bio: 'A trained vocalist and music educator with 6+ years of experience in pure vocal training, Hindustani classical and light or Bollywood styles, blending traditional riyaz with modern vocal technique.' },
    ],
  },
  {
    tier: 'Elite',
    note: 'Premium pricing',
    people: [
      { name: 'Jashandeep', focus: 'Guitar · contemporary', photo: '/img/instr/jashandeep.jpg', bio: 'A guitarist and music educator with over nine years of experience, combining strong fundamentals with practical, enjoyable learning so students find their own voice on the instrument.' },
      { name: 'Vikas Kumar', focus: 'Piano · production', photo: '/img/instr/vikas-kumar.jpg', bio: 'A pianist, sound engineer, music producer and educator with 8+ years across piano performance, audio engineering and studio production.' },
      { name: 'Prateek Das', focus: 'Drums · percussion', photo: '/img/instr/prateek-das.jpg', bio: 'A drum educator, audio engineer and music producer with 10+ years across live performance and studio production.' },
      { name: 'Biswadeep Dey', focus: 'Guitar · contemporary', photo: '/img/instr/biswadeep-dey.jpg', bio: 'A contemporary guitarist and performer on electric and acoustic guitar, with a diploma in contemporary music performance.' },
      { name: 'Parikshit', focus: 'Guitar · western', photo: '/img/instr/parikshit.jpg', bio: 'A western-music guitarist, performer and session musician specialising in contemporary music and modern harmony.' },
      { name: 'Arun Kumar Mishra', focus: 'Tabla & dholak', photo: '/img/instr/arun-kumar-mishra.jpg', bio: 'A tabla and dholak instructor, grounding students in the rhythm of Hindustani classical music.' },
      { name: 'Seema Jha', focus: 'Hindustani classical vocals', photo: '/img/instr/seema-jha.jpg', bio: 'A Hindustani classical vocalist and mentor.' },
      { name: 'Jatin Singh', focus: 'Bollywood & light vocals', photo: '/img/instr/jatin-singh.jpg', bio: 'A light-music and Bollywood vocalist, and Hindustani classical vocal instructor.' },
      { name: 'Amit Kumar', focus: 'Hindustani classical vocals', photo: '/img/instr/amit-kumar.jpg', bio: 'A Hindustani classical vocal instructor.' },
      { name: 'Tanvi Shukla', focus: 'Classical & western vocals', photo: '/img/instr/tanvi-shukla.jpg', bio: 'A Hindustani classical and western vocal instructor.' },
      { name: 'Sakshi Yadav', focus: 'Classical & light-music vocals', photo: '/img/instr/sakshi-yadav.jpg', imgPos: '50% 40%', modalPos: '50% 38%', bio: 'A passionate Hindustani classical and light-music vocalist and educator with 10+ years of experience, specialising in vocal technique, raga, taal and expressive styles like thumri, ghazal and semi-classical.' },
    ],
  },
]

/** Which communities show tier-grouped consultation lanes (else category lanes). */
export const consultLanesByCommunity: Record<string, LifecrafterTierLane[]> = {
  rhythm: musicInstructorLanes,
  zen: zenInstructorLanes,
}

/* ---- Cosmos "book a consultation": real Premium & Elite astrologers -------
   Categorised per the client's master pricing sheet. Each name is clickable and
   opens a bilingual (EN + Hindi) bio pop-up. Photos are pending from the client's
   Drive; entries without a real photo fall back to a profile-icon avatar. ----- */
export type ConsultCrafter = {
  name: string
  /** first name shown in the compact lane row; pop-up uses the full `name` */
  displayName: string
  tier: 'Premium' | 'Elite'
  focus: string
  years?: string
  photo?: string
  /** object-position for the round lane avatar (default center) */
  imgPos?: string
  about: string[]
  aboutHi?: string[]
}
export const cosmosConsultTiers: { tier: 'Premium' | 'Elite'; original: string; price: string; note: string }[] = [
  { tier: 'Premium', original: '₹1,100', price: 'from ₹499', note: 'until 30 Sept' },
  { tier: 'Elite', original: '₹3,100', price: 'from ₹999', note: 'until 30 Sept' },
]
export const cosmosConsultCrafters: ConsultCrafter[] = [
  /* ---- Premium ---- */
  /* Acharya Poonam Gaur & Vivek Kush moved to the mentor panel ("Book
     consultation"), so they are intentionally not listed here. */
  {
    name: 'Dr. Vijay Kumar Pathak',
    displayName: 'Vijay',
    tier: 'Premium',
    focus: 'Astrology & Vaastu Consultant',
    years: '20+ Years',
    photo: '/img/astro-vijay.jpg',
    about: ['Dr. Vijay Kumar Pathak is a renowned astrologer and Vaastu consultant with over 20 years of experience, recognised for his exceptional contributions to Vaastu Science and his unique ability to integrate ancient wisdom with modern scientific understanding. He is an award-winning expert, honoured with honorary doctorates and distinguished recognition from the Government of Uttarakhand for his outstanding work in the field.'],
    aboutHi: ['डॉ. विजय कुमार पाठक एक जाने-माने ज्योतिषी और वास्तु सलाहकार हैं, जिन्हें 20 से ज़्यादा वर्षों का अनुभव है। उन्हें वास्तु विज्ञान में उनके बेहतरीन योगदान और प्राचीन ज्ञान को आधुनिक वैज्ञानिक समझ के साथ जोड़ने की उनकी अनोखी क्षमता के लिए जाना जाता है। वे एक अवॉर्ड-विनिंग एक्सपर्ट हैं, जिन्हें इस क्षेत्र में उनके शानदार काम के लिए उत्तराखंड सरकार से मानद डॉक्टरेट और खास सम्मान मिला है।'],
  },
  {
    name: 'Deepak Gupta',
    displayName: 'Deepak',
    tier: 'Premium',
    focus: 'Lal Kitab & KP Astrology',
    photo: '/img/astro-deepak.jpg',
    about: ['Deepak Gupta is an experienced astrologer and educator specialising in Lal Kitab, Mobile Numerology and KP Astrology. With years of dedicated study and teaching, he has guided thousands of students in India and abroad. His practical, case-based approach helps individuals understand planetary influences, analyse horoscopes and apply appropriate astrological remedies with clarity and confidence.'],
    aboutHi: ['दीपक गुप्ता लाल किताब, मोबाइल न्यूमरोलॉजी और KP ज्योतिष के जानकार एवं अनुभवी ज्योतिषी व शिक्षक हैं। कई सालों की समर्पित पढ़ाई और अध्यापन के ज़रिए उन्होंने भारत और विदेशों में हज़ारों छात्रों का मार्गदर्शन किया है। उनका व्यावहारिक और केस-आधारित तरीका लोगों को ग्रहों के प्रभाव को समझने, कुंडली का विश्लेषण करने और सही ज्योतिषीय उपाय अपनाने में मदद करता है।'],
  },
  {
    name: 'Puneet Garg',
    displayName: 'Puneet',
    tier: 'Premium',
    focus: 'Vastu & KP Astrology',
    years: '18+ Years',
    photo: '/img/astro-puneet.jpg',
    about: ['Puneet Garg is an astrologer, Vastu consultant and researcher with 18+ years of experience, specialising in Vastu, KP Astrology and the Nakshatra System. He has trained 1,000+ professional astrologers and guided individuals, families and businesses with personalised, practical solutions. His research-driven approach combines birth-chart analysis with Vastu for guidance aligned to each client’s unique planetary influences and goals.'],
    aboutHi: ['पुनीत गर्ग एक ज्योतिषी, वास्तु सलाहकार और रिसर्चर हैं, जिन्हें वास्तु, KP ज्योतिष और नक्षत्र सिस्टम में 18 से ज़्यादा सालों का अनुभव है। उन्होंने 1,000 से ज़्यादा प्रोफेशनल ज्योतिषियों को ट्रेनिंग दी है और लोगों, परिवारों तथा व्यवसायों को खास एवं प्रैक्टिकल समाधान देकर मार्गदर्शन किया है। रिसर्च पर आधारित उनके तरीके में जन्म-कुंडली के विश्लेषण और वास्तु का मेल होता है।'],
  },
  /* ---- Elite ---- */
  {
    name: 'Asha Tyagi',
    displayName: 'Asha',
    tier: 'Elite',
    focus: 'Vedic Astrology & Palmistry',
    years: '31+ Years',
    photo: '/img/astro-asha.jpg',
    imgPos: 'center 30%',
    about: ['Asha Tyagi is a renowned astrologer, educator, author and legal professional with 31+ years of experience in Vedic astrology, palmistry and birth chart analysis. An award-winning teacher and television personality, she is known for her published works and practical, spiritually grounded guidance that empowers informed decisions.'],
    aboutHi: ['आशा त्यागी वैदिक ज्योतिष, हस्तरेखा विज्ञान और जन्म कुंडली विश्लेषण में 31+ वर्षों के अनुभव के साथ एक प्रसिद्ध ज्योतिषी, शिक्षक, लेखक और कानूनी पेशेवर हैं। एक पुरस्कार विजेता शिक्षिका और टेलीविजन हस्ती, वह अपने प्रकाशित कार्यों और व्यावहारिक, आध्यात्मिक रूप से आधारित मार्गदर्शन के लिए जानी जाती हैं।'],
  },
  {
    name: 'Dr. Hardeep Sidana',
    displayName: 'Hardeep',
    tier: 'Elite',
    focus: 'Vedic, KP & Nadi Astrology',
    photo: '/img/astro-hardeep.jpg',
    about: ['Dr. Hardeep Sidana is a qualified astrologer specialising in Vedic, KP, KB and Nakshatra Nadi astrology, and Vastu, known for his precise and practical approach to astrological guidance.'],
    aboutHi: ['डॉ. हरदीप सिडाना वैदिक, KP, KB, नक्षत्र नाड़ी ज्योतिष और वास्तु में माहिर एक योग्य ज्योतिषी हैं, जो ज्योतिषीय मार्गदर्शन के लिए अपने सटीक और व्यावहारिक नज़रिए के लिए जाने जाते हैं।'],
  },
  {
    name: 'Sonali Malhotra',
    displayName: 'Sonali',
    tier: 'Elite',
    focus: 'Professional Astrologer',
    photo: '/img/astro-sonali.jpg',
    about: ['Sonali Malhotra is a professional astrologer helping you navigate career, relationships, health and life decisions through practical and insightful astrology. She translates planetary influences into clear guidance for greater self-understanding and confident decisions.'],
    aboutHi: ['सोनाली मल्होत्रा एक प्रोफेशनल ज्योतिषी हैं। वे व्यावहारिक और गहरी ज्योतिषीय समझ के ज़रिए करियर, रिश्तों, सेहत और ज़िंदगी से जुड़े फ़ैसले लेने में आपकी मदद करती हैं और ग्रहों के असर को साफ़-सुथरी सलाह में बदलती हैं, ताकि आप आत्मविश्वास के साथ फ़ैसले ले सकें।'],
  },
  {
    name: 'Alok Gautam',
    displayName: 'Alok',
    tier: 'Elite',
    focus: 'KP Astrology & Modern Vaastu',
    years: '25+ Years',
    photo: '/img/astro-alok.jpg',
    about: ['Alok Gautam is an experienced KP Astrologer and Modern Vaastu Consultant with 25+ years of professional practice. He has conducted 3,000+ Vaastu site visits and trained approximately 8,000 students, focusing on practical, non-demolition Vaastu corrections for homes and businesses.'],
    aboutHi: ['आलोक गौतम एक अनुभवी केपी ज्योतिषी और मॉडर्न वास्तु कंसल्टेंट हैं, जिन्हें 25+ वर्षों का पेशेवर अनुभव है। उन्होंने 3,000+ वास्तु साइट विज़िट की हैं और लगभग 8,000 विद्यार्थियों को प्रशिक्षण दिया है, तथा बिना तोड़फोड़ किए व्यावहारिक वास्तु सुधार पर केंद्रित रहते हैं।'],
  },
  {
    name: 'Manjuu Sharma',
    displayName: 'Manju',
    tier: 'Elite',
    focus: 'Astro-Numerology & Vedic',
    years: '12+ Years',
    photo: '/img/astro-manju.jpg',
    about: ['Manjuu Sharma is a renowned Astro-numerologist and third-generation Vedic Astrology practitioner with 12+ years of experience. She specialises in Vedic Astrology, Numerology, Birth Chart Analysis, Palmistry, Vastu, Gemstone Consultation, Marriage Compatibility and Vedic Remedies, combining traditional wisdom with practical guidance to help people find clarity, positivity and balance.'],
    aboutHi: ['मंजु शर्मा 12+ वर्षों के अनुभव के साथ एक प्रसिद्ध एस्ट्रो-न्यूमरोलॉजिस्ट और तीसरी पीढ़ी की वैदिक ज्योतिष चिकित्सक हैं। वे वैदिक ज्योतिष, अंक ज्योतिष, जन्म कुंडली विश्लेषण, हस्तरेखा विज्ञान, वास्तु, रत्न परामर्श, विवाह अनुकूलता और वैदिक उपचार में विशेषज्ञ हैं और पारंपरिक ज्ञान को व्यावहारिक मार्गदर्शन के साथ जोड़ती हैं।'],
  },
  {
    name: 'Acharya Sagar Ji',
    displayName: 'Sagar',
    tier: 'Elite',
    focus: 'Vedic & Medical Astrology',
    years: '17+ Years',
    photo: '/img/astro-sagar.jpg',
    about: ['Acharya Sagar Ji is a seasoned astrologer with approximately 17 years of experience, specialising in Vedic Astrology, Medical Astrology, Numerology, Horary Astrology, Varga Charts and traditional remedial practices. Based in Ujjain, Madhya Pradesh, he is also an experienced teacher who conducts astrology workshops and courses.'],
    aboutHi: ['आचार्य सागर जी लगभग 17 वर्षों के अनुभव वाले एक अनुभवी ज्योतिषी हैं, जो वैदिक ज्योतिष, मेडिकल ज्योतिष, अंक ज्योतिष, प्रश्न ज्योतिष, वर्ग चार्ट और पारंपरिक उपाय पद्धतियों में माहिर हैं। मध्य प्रदेश के उज्जैन में रहने वाले आचार्य जी एक अनुभवी शिक्षक भी हैं और ज्योतिष पर वर्कशॉप व कोर्स आयोजित करते हैं।'],
  },
]

/* ---- Cosmos "Join your circle": four free community circles -------------- */
export type CosmosCircle = { name: string; short: string; blurb: string; icon: IconName; image: string; members: string }
export const cosmosCircles: CosmosCircle[] = [
  { name: 'Astrology Circle', short: 'Astrology', blurb: 'Weekly readings, chart talk and transit check-ins with fellow members.', icon: 'sun', image: '/img/circle-astrology.svg', members: '3,200' },
  { name: 'Vastu Circle', short: 'Vastu', blurb: 'Practical space and direction tips for homes and workplaces that sit right.', icon: 'mapPin', image: '/img/circle-vastu.svg', members: '1,450' },
  { name: 'Tarot Circle', short: 'Tarot', blurb: 'Beginner-friendly card pulls and gentle guidance, read with you, not at you.', icon: 'star', image: '/img/circle-tarot.svg', members: '2,100' },
  { name: 'Healing Circle', short: 'Healing', blurb: 'A calm space for energy work, meditation and quiet restorative practice.', icon: 'heart', image: '/img/circle-healing.svg', members: '1,780' },
]

export type CommunityPage = {
  hue: import('@/styles/lifecraft').CommunityHue
  videoLayout?: 'single' | 'reels' | 'carousel'
  videos?: { src: string; poster: string }[]
  people?: CommunityPeople
  types?: CommunityTypes
  daily?: {
    kicker: string
    title: string
    intro: string
    items: { when: string; icon: IconName; title: string; teaser: string; body: string[] }[]
  }
  /* Music Explorer Challenge: the Rhythm daily quiz. Questions and scoring
     live in src/data/musicQuiz.ts; this only drives the section copy + CTA. */
  quiz?: {
    kicker: string
    title: string
    intro: string
    cta: string
  }
  /* bookable programs: a rail of cards, each with the LifeCrafters who run it
     and a "book a slot" flow (pick a crafter, then a backend booking step). */
  programs?: {
    kicker: string
    title: string
    intro: string
    items: { name: string; blurb: string; crafters: { name: string; craft: string; photo: string }[] }[]
  }
  /* optional overrides for the events section heading (per community) */
  eventsKicker?: string
  eventsTitle?: string
  marketplace?: MarketplaceData
  slug: string
  name: string
  topic: string
  members: string
  titleA: string // display line before the lime word
  titleWord: string // lime-highlighted word
  intro: string
  paths: string[]
  image: string
  gallery?: string[]
  highlights?: { icon: IconName; title: string; body: string }[]
  journeys?: {
    kicker: string
    title: string
    intro: string
    stories: { age: string; line: string; who: string; photo: string }[]
    stages: { name: string; range: string; line: string; cta: string }[]
  }
  events: { day: string; time: string; kicker: string; title: string; blurb: string }[]
  voice?: { quote: string; who: string }
  host?: { name: string; craft: string; years: string; photo: string }
}

export const communityPages: Record<string, CommunityPage> = {
  zen: {
    hue: communityHues.sage,
    slug: 'zen',
    name: 'Zen',
    topic: 'Yoga',
    members: '7,300',
    titleA: 'find your',
    titleWord: 'stillness',
    intro: 'A calm daily practice guided by verified teachers: movement for every body, at your pace. Breath first, posture second, stillness always.',
    paths: ['Hatha', 'Breathwork', 'Restorative', 'Mobility'],
    image: '/img/yoga-class.jpg',
    gallery: ['/img/gen/hero-park-yoga.jpg', '/img/gen/hero-meditation.jpg', '/img/gen/card-zen.jpg'],
    videoLayout: 'reels',
    videos: [
      { src: '/video/yoga-bhaskar.mp4', poster: '/img/yoga-bhaskar.jpg' },
      { src: '/video/yoga-sumeet.mp4', poster: '/img/yoga-sumeet.jpg' },
      { src: '/video/yoga-naresh.mp4', poster: '/img/yoga-naresh.jpg' },
      { src: '/video/yoga-nayyan.mp4', poster: '/img/yoga-nayyan.jpg' },
    ],
    eventsTitle: 'Events and programs',
    people: {
      mentors: [
        {
          name: 'Bhaskar Yogacharya', craft: 'Yogi, Healer & Life Coach', years: '18+ yrs', photo: '/img/mentor-bhaskar.jpg', focus: 'center top', modalFocus: 'center 30%',
          line: 'Blends ancient yoga with modern psychology to guide deep, self-realising practice.',
          badge: 'Top rated', location: 'Rishikesh, India', languages: 'English, Hindi', rating: 4.9, reviews: 276,
          specialties: ['Chakra & Kundalini', 'Meditation', 'Kids Yoga & Vedanta'],
          stats: [
            { value: '5,000+', label: 'Sessions' },
            { value: '100k+', label: 'Followers' },
            { value: '30M+', label: 'Views' },
            { value: '18+', label: 'Years' },
          ],
          about: [
            'Bhaskar Yogacharya is a renowned yogi, healer and life coach specialising in Chakra and Kundalini practices, with close to two decades of experience guiding thousands towards self-realisation.',
            'Born in Rishikesh, he pairs ancient wisdom with modern psychology, currently pursuing his PhD, and has led wellness programmes for global corporations and public figures.',
          ],
          philosophy: 'Yoga is the union of body, mind and spirit. When we align within, we transform and inspire others to do the same.',
          whyLearn: ['Chakra & Kundalini specialist', 'Integrates modern psychology with yoga', 'Corporate mindfulness expert', 'Holistic healing & self-realisation', 'Kids Yoga & Vedanta teacher'],
        },
        {
          name: 'Dr. Sumeet Kaur', craft: 'Yoga Alliance Certified Trainer', years: '15+ yrs', photo: '/img/mentor-sumeet.jpg', focus: 'center 70%',
          line: 'Alignment-first Hatha, Vinyasa and Restorative yoga, informed by a medical background.',
          badge: 'Expert', location: 'Chandigarh, India', languages: 'English, Hindi, Punjabi', rating: 4.9, reviews: 156,
          specialties: ['Hatha & Vinyasa', 'Restorative & Yin', 'Power Yoga'],
          stats: [
            { value: '1,500+', label: 'Sessions' },
            { value: '2,000+', label: 'Lives impacted' },
            { value: '15+', label: 'Years' },
          ],
          about: [
            'Dr. Sumeet Kaur is a Yoga Alliance Certified Trainer with over 15 years teaching across private and group settings, specialising in Hatha, Vinyasa, Restorative, Yin-Yang and Power Yoga with a focus on alignment, flexibility and breath.',
            'Her background in Homeopathic Medicine and Surgery lets her deliver safe, adaptable and results-oriented sessions tailored to individual goals.',
          ],
          philosophy: 'Yoga is not about touching your toes. It’s about what you learn on the way down.',
          whyLearn: ['Yoga Alliance Certified Trainer', 'Specialises in multiple yoga styles', 'Focus on alignment, flexibility & breath', 'Personalised, goal-oriented programs', 'Safe, adaptable, evidence-informed'],
        },
        {
          name: 'Dr. Naresh Pathak', craft: 'Wellness Coach & Yoga Therapist', years: '30+ yrs', photo: '/img/mentor-naresh.jpg', focus: 'center top', modalFocus: 'center 40%',
          line: 'Corporate wellness and yoga therapy that brings balance to every part of life.',
          badge: 'Senior teacher', location: 'Gurugram, India', languages: 'English, Hindi', rating: 4.9, reviews: 142,
          specialties: ['Corporate wellness', 'Yoga therapy', 'Holistic healing'],
          stats: [
            { value: '3,000+', label: 'Sessions' },
            { value: '5,000+', label: 'Lives impacted' },
            { value: '30+', label: 'Years' },
          ],
          about: [
            'Dr. Naresh Pathak is an expert wellness coach, yoga therapist and mentor with over 30 years of experience in corporate wellness consultancy.',
            'Recognised as a Yoga Chikitsaka by the International Yoga Association, he bridges ancient yogic practice with the needs of the modern workplace to build resilience and well-being.',
          ],
          philosophy: 'Yoga is not just about postures, it’s about bringing balance into every aspect of life.',
          whyLearn: ['Corporate wellness expert, 30+ years', 'Recognised Yoga Chikitsaka', 'Specialised in yoga therapy & holistic healing', 'Bridges ancient yoga with modern needs', 'Empowers individuals and organisations'],
        },
        {
          name: 'Sudhir Attri', craft: 'Functional Nutritionist & Wellness Coach', years: '11+ yrs', photo: '/img/mentor-sudhir.jpg', focus: 'center top', modalFocus: 'center top',
          line: 'Functional nutrition for metabolic health, sleep, stress and lasting transformation.',
          badge: 'Expert', location: 'Delhi, India', languages: 'English, Hindi', rating: 4.8, reviews: 192,
          specialties: ['Metabolic health', 'Diabetes reversal', 'Nutrition & lifestyle'],
          stats: [
            { value: '1,200+', label: 'Sessions' },
            { value: '3,000+', label: 'Lives impacted' },
            { value: '11+', label: 'Years' },
          ],
          about: [
            'Sudhir Attri is a Functional Nutritionist and Holistic Wellness Coach with 11 years of experience, specialising in Type-2 diabetes remission and metabolic rejuvenation.',
            'He builds personalised nutrition plans for sleep, stress and mindset, helping clients achieve lasting transformation and sustainable metabolic health.',
          ],
          philosophy: 'Nourish your body wisely, live your life mindfully, and wellness becomes your natural state.',
          whyLearn: ['Type-2 diabetes & metabolic health expert', 'Personalised nutrition & lifestyle plans', 'Focus on sleep, stress & mind transformation', 'Collaborates with doctors for de-escalation', 'Proven track record on HbA1c levels'],
        },
        {
          name: 'Amita Srivastava', craft: 'Fitness & Wellness Professional', years: '15+ yrs', photo: '/img/mentor-amita.jpg', focus: 'center 5%', modalFocus: 'center 30%',
          line: 'Sustainable lifestyle change through nutrition, movement and mindset, tailored to PCOS, thyroid and weight goals.',
          badge: 'Expert', location: 'India', languages: 'English, Hindi', rating: 4.8, reviews: 168,
          specialties: ['PCOS & thyroid', 'Weight management', 'Nutrition & movement'],
          stats: [
            { value: '1,500+', label: 'Clients' },
            { value: '15+', label: 'Years' },
          ],
          about: [
            'Amita Srivastava is a fitness and wellness professional with over 15 years of experience supporting more than 1,500 clients.',
            'Her approach emphasises sustainable, practical lifestyle change, integrating nutrition, movement and mindset, tailored to individual needs like PCOS, thyroid and weight management.',
          ],
          philosophy: 'Small, sustainable changes, made consistently, become lasting wellness.',
          whyLearn: ['15+ years, 1,500+ clients', 'PCOS, thyroid & weight management', 'Integrates nutrition, movement & mindset', 'Practical, sustainable lifestyle change', 'Personalised to individual needs'],
        },
        {
          name: 'Ved Bhaskar', craft: 'Yoga Educator & Founder, Patanjali Yoga Kendra', years: '30+ yrs', photo: '/img/mentor-ved.jpg', focus: 'center 25%', zoom: 1.1,
          line: 'Three decades of classical yoga teaching, meditation camps and yoga education across India and abroad.',
          badge: 'Senior teacher', location: 'Visakhapatnam, India', languages: 'English, Telugu, Hindi', rating: 4.9, reviews: 118,
          specialties: ['Classical yoga', 'Meditation', 'Yoga education'],
          stats: [
            { value: '30+', label: 'Years' },
            { value: '4', label: 'Countries' },
            { value: '1995', label: 'Founded Kendra' },
          ],
          about: [
            'G. Veda Bhaskaram is a highly experienced yoga educator, lecturer and founder of Patanjali Yoga Kendra, with over three decades in yoga teaching and wellness education.',
            'A former Yoga Teacher at Kendriya Vidyalaya, Chennai and Yoga Lecturer at Andhra University, he has led yoga and meditation camps across India, Singapore, Malaysia and the USA, and developed audio-video content on yoga, relaxation, meditation and devotional music.',
          ],
          philosophy: 'Yoga, practised with discipline and devotion, steadies the mind and awakens the self.',
          whyLearn: ['30+ years teaching classical yoga', 'Founder, Patanjali Yoga Kendra (since 1995)', 'Former lecturer, Andhra University', 'Camps across India, Singapore, Malaysia & USA', 'P.G. Diploma & International Yoga TTC'],
        },
      ],
      mentorNote: 'Pricing on request: our team confirms each mentor\u2019s available slots and rates before you book.',
      categories: [
        { category: 'Asana & alignment', startsAt: '\u20B9499', people: [
          { name: 'Kavita Rane', line: 'Chair yoga and safe backs', photo: '/img/portrait-5.jpg' },
          { name: 'Dev Malhotra', line: 'Mobility for stiff mornings', photo: '/img/portrait-4.jpg' },
        ]},
        { category: 'Breath & rest', startsAt: '\u20B9399', people: [
          { name: 'Anahita Shroff', line: 'Yoga nidra, gently guided', photo: '/img/portrait-1.jpg' },
          { name: 'Ravi Kishore', line: 'Pranayama foundations', photo: '/img/portrait-6.jpg' },
        ]},
        { category: 'Everyday practice', startsAt: '\u20B9299', people: [
          { name: 'Lakshmi Pillai', line: 'Morning flows, all paces', photo: '/img/portrait-2.jpg' },
          { name: 'Tarun Bhatt', line: 'Desk stretches that stick', photo: '/img/portrait-3.jpg' },
        ]},
      ],
    },
    types: {
      kicker: 'Lifecraft4U Programs',
      title: 'Find the practice that fits you.',
      intro: 'Different paths, one purpose. Explore yoga styles, breathing practices and pathways designed for different goals, energy levels and stages of practice.',
      labelA: 'Best for',
      labelB: 'What it offers',
      items: [
        { name: 'Yoga Foundations & Movement', icon: 'leaf', image: '/img/school/foundations.jpg', body: 'Build strength, flexibility and confident movement.', tagline: 'Start strong, start safe', what: 'A foundational course designed to build a strong, safe and comprehensive practice. Ideal for beginners or anyone solidifying the essentials, key asanas, alignment, core principles and basic pranayama, for a practice that lasts.', a: ['Asana basics', 'Form & alignment', 'Breath awareness', 'Core stability'], b: ['Core asanas & alignment on 25+ postures', 'Progressive functional strength & mobility', 'Breath & flow fundamentals', 'A sustainable, day-one-friendly path'], note: 'Choose your path: beginner, intermediate or advanced foundations.' },
        { name: 'Mind, Sleep & Relaxation', icon: 'sparkle', image: '/img/school/mind-sleep.jpg', body: 'Calm your mind and deepen your relaxation.', tagline: 'Rest, restored', what: 'A deeply restorative program designed to calm the nervous system, improve sleep quality and foster mental clarity. Using gentle practices, breathwork and guided relaxation, this series suits all levels seeking relief from stress and insomnia, with tools for inner stillness and sustainable well-being.', a: ['Stress reduction', 'Sleep quality', 'Inner stillness', 'Mental clarity'], b: ['Mindful breathwork to quickly calm the mind', 'Sleep-hygiene practices for deep, restful sleep', 'Structured, guided relaxation to release tension', 'Gentle practices for every level'], note: 'Beginner, intermediate and advanced tracks in mind, sleep & relaxation.' },
        { name: 'Preventive Health & Vitality', icon: 'shield', image: '/img/school/preventive.jpg', body: 'Support everyday health and long-term wellbeing.', tagline: 'Vitality for the long run', what: 'A comprehensive program designed for long-term well-being and daily vitality. It integrates pranayama for lung strengthening, gentle movement for joint mobility and specific practices for pain management, addressing neck, shoulder, lower back, sciatica and knee care, with consistent breath regulation and effective stress management for a healthier life.', a: ['Lung health', 'Pain management', 'Daily vitality', 'Stress reduction'], b: ['Pranayama & lung strengthening', 'Pain relief & joint mobility for neck, back and knees', 'Gentle yoga & stress reduction', 'Consistent breath regulation'], note: 'Beginner, intermediate and advanced tracks in preventive health.' },
        { name: "Women's Wellness & Metabolism", icon: 'heart', image: '/img/school/womens.jpg', body: 'Support hormones, metabolic and life-stage wellness.', tagline: 'Health from the inside out', what: 'A targeted program for women of all life stages. Specific sequences and lifestyle guidance address hormonal fluctuations, enhance metabolism, and support weight management, digestive wellness and emotional balance.', a: ['Hormonal balance', 'Metabolic support', 'Digestive health', 'Weight management'], b: ['Hormonal & metabolic support', 'Digestive wellness', 'Emotional & sleep balance', 'Sequences for every life stage'], note: 'Beginner to advanced tracks for endocrine and metabolic wellness.' },
        { name: 'Active Ageing 50+', icon: 'sun', image: '/img/school/active-ageing.jpg', body: 'Stay mobile, active and energised as you age.', tagline: 'Vitality beyond 50', what: 'A mindful program for vitality and mobility beyond 50. Gentle movement, balance, cognitive wellness and effective pain management, tailored to restore mobility and support healthy ageing with personalised care.', a: ['Joint mobility', 'Cognitive wellness', 'Mobility restoration', 'Balance improvement'], b: ['Gentle stretching & joint mobility', 'Cognitive & mental wellness', 'Pain management & recovery', 'Seated and supported options'], note: 'From very simple seated practice to adaptive advanced sequences.' },
        { name: "Men's Wellness", icon: 'compass', image: '/img/school/mens.jpg', body: 'Support pelvic circulation, hormone balance and vitality.', tagline: 'Holistic male wellness', what: 'A dedicated program supporting overall male health through a holistic approach, targeted practices to improve pelvic circulation, balance hormones, reduce stress and support fertility and vitality.', a: ['Male vitality', 'Stress reduction', 'Pelvic circulation', 'Hormone balance'], b: ['Pelvic circulation through targeted postures', 'Hormone balance, naturally', 'Stress reduction & deep relaxation', 'Practices for overall male health'], note: 'Beginner to advanced tracks in men\u2019s wellness and functional movement.' },
        { name: 'Lifestyle Disorders', icon: 'orbit', image: '/img/school/lifestyle.jpg', body: 'Manage lifestyle conditions with integrated fitness and diet.', tagline: 'Reset your baseline', what: 'A comprehensive program designed to support the management of complex lifestyle-related health conditions. Personalized approaches integrate physical fitness, macro-alignment and digestive-health strategies to address diabetes and metabolic disorders, restore healthy glucose metabolism and support glycemic control.', a: ['Diabetes remission', 'Metabolic health', 'Gut health', 'Glucose control'], b: ['Metabolic reset & glucose control', 'Gut soothing & microbiome repair', 'Endocrine health & muscle hypertrophy', 'Personalised, protocol-driven care'], note: 'From foundational protocols to advanced metabolic care.' },
        { name: 'Weight Loss', icon: 'star', image: '/img/school/weight-loss.jpg', body: 'Personalised programs for sustainable loss and metabolic health.', tagline: 'Sustainable, not crash', what: 'A comprehensive program designed to support sustainable weight reduction through integrated physical and dietary strategies. These personalized approaches address weight management and conditions like diabetes and PCOD using macro-balancing and specific dietary protocols.', a: ['Weight reduction', 'Diabetes control', 'PCOD management', 'Hormonal balance'], b: ['Sustainable weight reduction with tailored macros', 'Metabolic reset & glycemic control', 'Hormonal & PCOD support', 'Consistent, guided exercise'], note: 'Beginner, intermediate and advanced weight-loss tracks.' },
      ],
    },
    marketplace: {
      kicker: 'Alliances & partners',
      title: 'The Zen marketplace.',
      intro: 'Studios, teacher trainings, props and retreats from partners who take practice seriously.',
      categories: ['Studios & trainings', 'Mats & props', 'Retreats', 'At-home sessions'],
      items: [
        { kind: 'Training', title: 'Foundations Teacher Training', partner: 'Yoga Vidya Gurukul', blurb: 'A 100-hour foundation for serious students, not just future teachers.', meta: '12 weeks · hybrid', price: '\u20B928,000' },
        { kind: 'Props', title: 'Mats, Bolsters & Belts', partner: 'Sattva Props Co.', blurb: 'Honest cotton props made for older knees and real floors.', meta: 'Ships across India', price: 'From \u20B91,200' },
        { kind: 'Retreat', title: 'Silence & Asana Weekend', partner: 'Vana Retreats', blurb: 'Two nights of slow practice, river walks and very good food.', meta: '2 nights · Rishikesh', price: '\u20B914,500' },
        { kind: 'At-home', title: 'Private Sessions at Home', partner: 'GharYoga', blurb: 'A verified teacher comes to your living room, mat included.', meta: 'Metro cities', price: '\u20B9799 a session' },
        { kind: 'Studio', title: 'Partner Studio Passes', partner: 'Shala Collective', blurb: 'Drop-in passes at partner studios across five cities.', meta: '5 cities', price: 'From \u20B9350' },
        { kind: 'Books', title: 'The Breath Companion', partner: 'Pranayama Press', blurb: 'A plain-language guide to every breath practice we teach.', meta: 'Paperback', price: '\u20B9650' },
      ],
      note: 'A studio, maker or retreat? Partner tiers open on the LifeCrafter Program.',
    },
    events: [
      { day: 'Mon', time: '6:30', kicker: 'Online · live on LifeCraft4U', title: 'Sunrise Flow, Live', blurb: 'Start the week on the mat. Twenty minutes, all paces.' },
      { day: 'Thu', time: '7:00', kicker: 'Online · live on LifeCraft4U', title: 'Chair Yoga for Desk Years', blurb: 'For backs that have sat through long careers.' },
      { day: 'Sun', time: '8:00', kicker: 'In person · Bengaluru', title: 'Park Practice & Chai', blurb: 'Mats on grass, then chai under the trees.' },
    ],
    voice: { quote: 'My knees showed up. My excuses didn\u2019t.', who: 'Suresh, 58' },
  },
  spirituality: {
    videoLayout: 'carousel',
    videos: [
      { src: '/video/community-demo.mp4', poster: '/img/spirituality.jpg' },
      { src: '/video/community-demo.mp4', poster: '/img/atmosphere-path.jpg' },
      { src: '/video/community-demo.mp4', poster: '/img/events-circle.jpg' },
    ],
    people: {
      mentors: [
        { name: 'Anand Kulkarni', craft: 'Gita & Vedanta', years: '24 yrs', photo: '/img/portrait-4.jpg', line: 'Reads a chapter a week with the circle, plainly and patiently.' },
        { name: 'Radha Venkatesan', craft: 'Mindfulness & stillness', years: '14 yrs', photo: '/img/portrait-1.jpg', line: 'Teaches secular sits with ancient roots. Five minutes is a real practice.' },
        { name: 'Mohan Das', craft: 'Satsang & scripture', years: '11 yrs', photo: '/img/portrait-3.jpg', line: 'Hosts the weekly satsang and keeps every voice welcome in it.' },
      ],
      mentorNote: 'Pricing on request: our team confirms each mentor\u2019s available slots and rates before you book.',
      categories: [
        { category: 'Scripture & study', startsAt: '\u20B9499', people: [
          { name: 'Kamala Nair', line: 'Gita circle, verse by verse', photo: '/img/portrait-5.jpg' },
          { name: 'Lata Deshpande', line: 'Journalling the readings', photo: '/img/portrait-2.jpg' },
        ]},
        { category: 'Practice & stillness', startsAt: '\u20B9399', people: [
          { name: 'Sudha Menon', line: 'Mindful walks, slow mornings', photo: '/img/portrait-1.jpg' },
          { name: 'Jasleen Kaur', line: 'Kirtan and shabad circles', photo: '/img/portrait-2.jpg' },
        ]},
        { category: 'Festivals & seva', startsAt: '\u20B9299', people: [
          { name: 'Vinod Shah', line: 'Festivals, kept together', photo: '/img/portrait-4.jpg' },
          { name: 'Harpreet Singh', line: 'Seva days with your circle', photo: '/img/portrait-6.jpg' },
        ]},
      ],
    },
    journeys: {
      kicker: 'Life stage journeys',
      title: 'Real stories. Real transformations.',
      intro: 'Every stage of life asks its own questions. Meet people mid-answer.',
      stories: [
        { age: 'At 47', line: 'Questions became louder than routines.', who: 'A mother of two finds grounding in Gita reflections.', photo: '/img/portrait-2.jpg' },
        { age: 'At 58', line: 'I needed one moment of calm.', who: 'A consultant discovers his first pranayama between meetings.', photo: '/img/portrait-3.jpg' },
        { age: 'At 72', line: 'I became the teacher I was searching for.', who: 'A retired professor leads weekly virtual satsangs.', photo: '/img/portrait-4.jpg' },
      ],
      stages: [
        { name: 'Explorer', range: '18 to 35', line: 'Discover yourself. Start small. Stay curious.', cta: 'Begin' },
        { name: 'Transformer', range: '36 to 60', line: 'Re-anchor. Realign. Live with meaning.', cta: 'Re-anchor' },
        { name: 'Mentor', range: '60 and beyond', line: 'Share. Guide. Leave a legacy of wisdom.', cta: 'Guide others' },
      ],
    },
    types: {
      kicker: 'Schools of spirituality',
      title: 'Many paths. One truth.',
      intro: 'Explore, learn and find what resonates with you. Truth is one; sages call it by many names.',
      labelA: 'Start here if',
      labelB: "What you'll find",
      items: [
        { name: 'Sanatan Dharma', icon: 'sun', image: '/img/sub/spirit-sanatan.jpg', body: 'The eternal way: harmony with self, others and nature.', tagline: 'The eternal way', what: 'A timeless way of living in harmony with the self, others and nature. Rooted in ancient wisdom, open to all seekers.', a: ['You grew up around it and want to go deeper', 'Dharma, karma and moksha feel like home words', 'You want one framework for daily living', 'Ritual and philosophy both call to you'], b: ['Core ideas in five minutes', 'A gentle 30-day beginner path', 'Gita and Upanishad circles', 'Festivals kept together'], note: 'Philosophy: dharma, karma, moksha. Open, inclusive, universal.' },
        { name: 'Sikh Wisdom', icon: 'heart', image: '/img/sub/spirit-sikh.jpg', body: 'Honest work, shared kitchens and kirtan that carries the room.', tagline: 'Seva and song', what: 'The path of the Gurus: honest work, service without headlines, and shabad kirtan that carries the whole room.', a: ['Kirtan moves you before the words do', 'Service feels like prayer to you', 'You want community at the centre', 'A langar once taught you something'], b: ['Shabad circles', 'Seva days with your circle', 'Gurbani, gently read', 'Stories of the ten Gurus'], note: 'Everyone sits together; that is the point.' },
        { name: 'Buddhist Path', icon: 'leaf', image: '/img/sub/spirit-buddhist.jpg', body: 'Attention, compassion and the art of watching the mind.', tagline: 'The middle way', what: 'Practice over belief: attention, compassion and the quiet art of watching the mind do what minds do.', a: ['You want practice more than doctrine', 'The breath keeps calling you back', 'Suffering and its causes feel worth studying', 'Small daily disciplines suit you'], b: ['Guided sits, morning and night', 'Sutta study, plain-spoken', 'Metta practice for hard weeks', 'Quiet retreat days'], note: 'Begin with ten minutes of sitting; everything else follows.' },
        { name: 'Jain Way', icon: 'shield', image: '/img/sub/spirit-jain.jpg', body: 'Radical gentleness: ahimsa, truth and travelling light.', tagline: 'Ahimsa first', what: 'A path of radical gentleness: non-violence in thought and act, truthfulness, and lightness of possession.', a: ['Ahimsa already shapes your choices', 'You want ethics you can practise daily', 'Fasting and stillness interest you', 'You prefer restraint to excess'], b: ['Paryushan kept together', 'Ethics circles', 'Mindful eating practice', 'Old texts, read slowly'], note: 'Small vows, kept kindly, change a life.' },
        { name: 'Gita Bodh', icon: 'star', image: '/img/sub/spirit-gita.jpg', body: 'The Gita, verse by verse: action without attachment.', tagline: 'The song of duty', what: 'The Bhagavad Gita read verse by verse: action without attachment, courage without noise, duty without despair.', a: ['One book, taken deeply, appeals to you', 'Questions of work and duty weigh on you', 'You want philosophy tied to daily life', "Arjuna's doubts feel familiar"], b: ['Chapter-a-week circles', 'Verse journals', 'Commentary across traditions', 'Living-the-Gita prompts'], note: 'No Sanskrit needed; every verse arrives translated and explained.' },
        { name: 'Interfaith Harmony', icon: 'users', image: '/img/sub/spirit-interfaith.jpg', body: 'Every tradition at one table: shared festivals, shared questions.', tagline: 'Many lamps, one light', what: 'A meeting place for every tradition: shared festivals, honest questions and the respect that makes both possible.', a: ['Your family holds more than one faith', 'You are curious across traditions', 'Dialogue matters to you', 'You celebrate everything, happily'], b: ['Festival exchanges', 'Comparative text circles', 'Shared meals', 'Moderated, respectful debate'], note: 'Disagreement is welcome here; disrespect is not.' },
        { name: 'Modern Mindfulness', icon: 'sparkle', image: '/img/sub/spirit-mindfulness.jpg', body: 'Ancient roots, secular practice, five minutes at a time.', tagline: 'Ancient, made daily', what: 'Secular practice drawn from old roots: attention, gratitude and rest, sized for modern lives.', a: ['You want the benefits without doctrine', 'Sleep and stress need tending', 'Science-friendly framing helps you', 'Five minutes is what you have'], b: ['Five-minute practices', 'Sleep and stress tools', 'Gratitude circles', 'Walking meditations'], note: 'Works alongside any faith, or none.' },
      ],
    },
    marketplace: {
      kicker: 'Alliances & partners',
      title: 'Trusted partnerships. Shared purpose.',
      intro: 'Institutions, teachers and service providers who uphold authentic wisdom, brought onto one shelf.',
      categories: ['Ashrams & learning', 'Retreats', 'Pilgrimage travel', 'Sacred products', 'Books & lifestyle', 'Counselling'],
      items: [
        { kind: 'Retreat', title: 'Gita Shibir, Residential', partner: 'Rishikesh Gita Vidyapeeth', blurb: 'Four quiet days of study, satsang and river mornings.', meta: '4 days · Rishikesh', price: '₹11,000' },
        { kind: 'Course', title: 'Sanskrit for Beginners', partner: 'Sharda Mahavidyalaya', blurb: 'Read the shlokas you already love, in their own script.', meta: '10 weeks · live online', price: '₹3,999' },
        { kind: 'Travel', title: 'Guided Pilgrimage: Kashi', partner: 'Yatra Sathi Travel', blurb: 'A small-group yatra with everything handled, gently.', meta: '5 days · small group', price: 'From ₹24,000' },
        { kind: 'Products', title: 'Cushions, Malas & Lamps', partner: 'Shanti Essentials', blurb: 'Meditation accessories and sacred products, honestly made.', meta: 'Ships across India', price: 'From ₹900' },
        { kind: 'Books', title: 'Wisdom Library Set', partner: 'Gita Press', blurb: 'Twelve classics for the home shelf, plainly translated.', meta: 'Set of 12', price: '₹1,500' },
        { kind: 'Counselling', title: 'One-on-one Counselling', partner: 'Antar Counselling Collective', blurb: 'Trained listeners for the seasons the scriptures do not cover.', meta: 'Online · 50 minutes', price: '₹1,200' },
      ],
      note: 'Run an ashram, school or service? Partner tiers open on the LifeCrafter Program.',
    },
    daily: {
      kicker: 'Daily engagement',
      title: 'Small steps. Big shifts.',
      intro: 'Your daily habit loop for inner growth. Tap a stop for a taste; the full loop lives inside the community.',
      items: [
        { when: 'Sunrise', icon: 'sun', title: 'Morning Meditation', teaser: 'Begin the day with clarity and calm', body: ['Morning Stillness: ten guided minutes before the house wakes.', 'Sit comfortably; the voice does the rest.', 'A new sit arrives every morning inside the community.'] },
        { when: 'With your chai', icon: 'star', title: 'Thought of the Day', teaser: 'One line to carry with you', body: ['"The mind is everything. What you think, you become."', 'Sit with it. Then go three levels deeper with the short commentary.', 'A new thought lands daily inside Spirituality.'] },
        { when: 'Five minutes', icon: 'leaf', title: '5-Minute Practice', teaser: 'Breath awareness, anywhere', body: ['A simple breathing practice to centre your mind.', 'In for four, hold for four, out for six. Repeat gently.', 'A different five-minute practice every day.'] },
        { when: 'A short listen', icon: 'mic', title: 'Guru Speaks', teaser: 'Wisdom, in shorts', body: ['"Be in the world, but not of it."', 'Two-minute clips from teachers across traditions.', "Today's short and its transcript live inside."] },
        { when: 'Before sleep', icon: 'sparkle', title: 'Reflection Prompt', teaser: 'A question for your journal', body: ['What did you hold on to today that you can release?', 'Three lines are enough. Keep them private, or share with your circle.', 'Prompts change nightly inside the community.'] },
      ],
    },
    hue: communityHues.saffron,
    slug: 'spirituality',
    name: 'Spirituality',
    topic: 'Seekers & Wisdom',
    members: '15,000',
    titleA: 'walk your',
    titleWord: 'path',
    intro:
      'Many roads, one shared respect. Bhakti mornings, Gita study, quiet seva, long silences. A spine of daily practice and a circle of fellow seekers who take the walk seriously and hold it lightly.',
    paths: ['Bhakti & kirtan', 'Gita study', 'Seva', 'Silence'],
    image: '/img/spirituality.jpg',
    gallery: ['/img/gen/hero-kirtan.jpg', '/img/gen/hero-chai.jpg', '/img/gen/hero-walk-group.jpg'],
    events: [
      { day: 'Sun', time: '7:00', kicker: 'Online · live on LifeCraft4U', title: 'Morning Kirtan Circle', blurb: 'Sing together as the day opens. No voice too small.' },
      { day: 'Wed', time: '8:00', kicker: 'Online · live on LifeCraft4U', title: 'Gita, Chapter Two', blurb: 'A slow read with Anand. Questions welcome, certainty optional.' },
      { day: 'Sat', time: '6:30', kicker: 'In person · Mumbai', title: 'Silent Beach Walk', blurb: 'An hour of quiet steps, then chai and conversation.' },
    ],
    voice: { quote: 'I came for the philosophy. I stayed because somebody saved me a seat.', who: 'Kamala, 61' },
    host: { name: 'Anand Kulkarni', craft: 'Gita teacher and long-time walker of quiet paths.', years: '24 yrs', photo: '/img/portrait-4.jpg' },
  },
  rhythm: {
    videoLayout: 'reels',
    videos: [
      { src: '/video/music-emmanuel.mp4', poster: '/img/music-emmanuel.jpg' },
      { src: '/video/music-akarshita.mp4', poster: '/img/music-akarshita.jpg' },
      { src: '/video/music-hira.mp4', poster: '/img/music-hira.jpg' },
      { src: '/video/music-seema.mp4', poster: '/img/music-seema.jpg' },
    ],
    quiz: {
      kicker: 'Daily engagement',
      title: 'The Music Explorer Challenge',
      intro: 'A quick daily quiz across Bollywood, Indian classical, folk and the craft of music. Answer, build a streak, earn coins, and see if you can rise to Music Maestro.',
      cta: 'Play today’s quiz',
    },
    people: {
      mentors: [
        {
          name: 'Dr. Akarshita Srivastava', craft: 'Hindustani Classical Vocal Mentor', photo: '/img/mentor-akarshita.jpg',
          line: 'Blends academic insight with traditional training in raga dynamics and voice culture.',
          specialties: ['Raga dynamics', 'Voice culture', 'Vocal storytelling'],
          about: [
            'As a Hindustani classical vocal mentor and scholar, I combine academic insight with traditional musical training to guide students in raga dynamics, voice culture and expressive vocal storytelling.',
            'I empower aspiring artists to master classical aesthetics, foster genuine artistic intuition, and preserve the deep, timeless heritage of Indian classical music.',
          ],
        },
        {
          name: 'Hira Gain', craft: 'Hindustani Classical Vocal Mentor', photo: '/img/mentor-hira.jpg',
          line: 'Guides raga purity, voice modulation and expressive improvisation, guru-shishya style.',
          specialties: ['Raga purity', 'Voice modulation', 'Improvisation'],
          about: [
            'As a Hindustani classical vocal mentor, I am dedicated to guiding students through the intricacies of raga purity, voice modulation and expressive improvisation.',
            'By blending traditional guru-shishya discipline with modern pedagogy, I help aspiring vocalists cultivate technical precision, soul-stirring depth and a profound connection to Indian musical heritage.',
          ],
        },
        {
          name: 'Emmanuel Samson Egbert', craft: 'Guitarist & Western Vocalist', photo: '/img/mentor-emmanuel.jpg', focus: 'center 20%',
          line: 'Immersive Western music blending rich vocal dynamics with expressive guitar.',
          specialties: ['Guitar technique', 'Western vocals', 'Stage presence'],
          about: [
            'As a versatile guitarist and Western vocalist, I craft immersive musical experiences blending rich vocal dynamics with expressive fretboard technique.',
            'Dedicated to performance and education, I inspire aspiring musicians to master vocal control, harmony, guitar proficiency and confident stage presence across diverse Western genres.',
          ],
        },
        {
          name: 'Sunetra Chakraborty', craft: 'Devotional, Light Music & Folk Vocalist', photo: '/img/mentor-sunetra.jpg', focus: 'center 20%',
          line: 'Soulful devotional and light-classical singing, steeped in spirituality and interfaith warmth.',
          specialties: ['Devotional & bhajan', 'Light music & folk', 'Interfaith singing'],
          about: [
            'A vocalist, podcaster and educator, I blend music, spirituality and meaningful conversation. From Rabindra Sangeet and Bengali songs to Hindi film and devotional music, my journey has been shaped from childhood by a home rich with monks, swamis and spiritual teachers.',
            'Under the guidance of Prajnanpurush Sri Sri Babathakur, I began rendering His revealed bhajans, and my singing seeks to carry listeners into serenity, contemplation and devotion. Through my interfaith conversations and the “Life N Living – Talent Unplugged” platform, I nurture harmony, culture and the universal values that unite us.',
          ],
        },
      ],
      mentorNote: 'Pricing on request: our team confirms each mentor\u2019s available slots and rates before you book.',
      categories: [
        { category: 'Vocals', startsAt: '\u20B9599', people: [
          { name: 'Meera Pathak', line: 'Bhajan circle, all voices', photo: '/img/portrait-2.jpg' },
          { name: 'Sunil Kalra', line: 'Riyaaz buddy check-ins', photo: '/img/portrait-4.jpg' },
        ]},
        { category: 'Instruments', startsAt: '\u20B9499', people: [
          { name: 'Anita Rao', line: 'Tabla from the first beat', photo: '/img/portrait-1.jpg' },
          { name: 'Farid Khan', line: 'Harmonium and open-mic host', photo: '/img/portrait-6.jpg' },
        ]},
        { category: 'Listening & writing', startsAt: '\u20B9299', people: [
          { name: 'Joseph D\u2019Lima', line: 'The Sunday listening club', photo: '/img/portrait-5.jpg' },
          { name: 'Nandini Iyer', line: 'The songwriting nook', photo: '/img/portrait-3.jpg' },
        ]},
      ],
    },
    marketplace: {
      kicker: 'Alliances & partners',
      title: 'Stronger together. Better music for everyone.',
      intro: 'We collaborate with music institutions and trusted brands for the best learning and equipment.',
      categories: ['Academies', 'Instruments', 'Gear & accessories', 'Studio & recording', 'At-home services'],
      items: [
        { kind: 'Academy', title: 'Hindustani Vocals, Level One', partner: 'Swaranjali Academy of Music', blurb: 'A structured foundation with weekly live classes.', meta: '8 weeks · live online', price: '₹6,999' },
        { kind: 'Instruments', title: 'Beginner Tanpura & Tabla Sets', partner: 'Darpan Musicals', blurb: 'Hand-tuned starter instruments, checked by our LifeCrafters.', meta: 'Ships across India', price: 'From ₹8,500' },
        { kind: 'Gear', title: 'Practice Amps & Recorders', partner: 'StudioBhai', blurb: 'Simple recording gear for your riyaaz check-ins.', meta: 'Member pricing', price: 'From ₹2,200' },
        { kind: 'At-home service', title: 'Instrument Tuning at Home', partner: 'SurSeva', blurb: 'A technician comes home; your harmonium breathes again.', meta: 'Metro cities', price: '₹499 a visit' },
        { kind: 'Academy', title: 'Carnatic Foundations', partner: 'Nada Vidyalaya', blurb: 'Varnams and geethams from first principles.', meta: '6 weeks · live online', price: '₹5,499' },
        { kind: 'Books', title: 'Raag Companion Handbooks', partner: 'Sangeet Prakashan', blurb: 'Raags, taals and compositions in plain language.', meta: 'Set of 3', price: '₹1,199' },
      ],
      note: 'An academy, maker or service? Partner tiers open on the LifeCrafter Program.',
    },
    /* Daily riyaaz hidden from the Rhythm community for now (per client) —
       CommunityBase guards on `page.daily`, so omitting it removes the section.
       Restore this block to bring the daily practice loop back.
    daily: {
      kicker: 'Your daily riyaaz',
      title: 'Practice a little. Every day.',
      intro: 'Grow for life. Tap a stop for a taste; the full riyaaz loop lives inside the community.',
      items: [
        { when: 'Morning', icon: 'mic', title: 'Raag of the Day', teaser: 'Today: Yaman, for the evening', body: ['Raag Yaman: evening, peaceful, devotional.', 'A raag of tranquillity that brings calm to the mind and clarity to the heart.', 'Listen and feel: three annotated recordings inside.'] },
        { when: 'Clap along', icon: 'orbit', title: 'Taal of the Day', teaser: 'Teentaal, 16 beats, 4 vibhags', body: ['The most widely used taal in Hindustani music.', 'Start the clap-along and find the sam together.', 'A new groove every day inside Rhythm.'] },
        { when: 'Two minutes', icon: 'star', title: 'Know Your Instrument', teaser: 'Today: the tabla', body: ['A pair of drums that is the heartbeat of Indian music.', 'Structure, sound and one beginner tip, in two minutes.', 'A different instrument every day.'] },
        { when: 'After practice', icon: 'heart', title: 'Riyaaz Journal', teaser: 'Your practice, remembered', body: ['A private log of what you practised and how it felt.', 'No streaks, no scores: just your own notes, kept safely.', 'Your LifeCrafter reads it before your next check-in, if you share it.'] },
      ],
    },
    */
    types: {
      kicker: 'Discover music genres',
      title: 'Every genre. Every emotion. Every you.',
      intro: 'Explore the rich world of music and find what moves you.',
      labelA: 'This is your lane if',
      labelB: "What you'll find",
      items: [
        { name: 'Hindustani Classical', icon: 'mic', image: '/img/sub/rhythm-hindustani.jpg', body: 'Raags and the timeless North Indian tradition.', tagline: 'The northern raag', what: 'Raags, taans and the timeless tradition of North Indian classical music, unfolding slowly and rewarding patience.', a: ['Focus and peace are what you seek', 'You love the slow unfolding of a raag', 'Riyaaz discipline appeals to you', 'You had lessons once, and miss them'], b: ['Raag-a-week circles', 'Riyaaz check-ins with video replies', 'Annotated listening sessions', 'Maestro masterclasses'], note: 'Great for focus and peace. Start with Yaman.' },
        { name: 'Carnatic Classical', icon: 'star', image: '/img/sub/rhythm-carnatic.jpg', body: 'Melody, rhythm and devotion from the southern stream.', tagline: 'The southern stream', what: 'Melody, rhythm and devotion in the rich heritage of South Indian classical music, where rigour and warmth meet.', a: ['Devotion and discipline both draw you', 'Kritis already play in your head', 'You want rigour with warmth', 'Tala patterns delight you'], b: ['Varnam foundations', 'Kriti circles', 'Layam practice sessions', 'Composer deep-dives'], note: 'Great for devotion and discipline. The trinity awaits.' },
        { name: 'Light & Semi-Classical', icon: 'sun', image: '/img/sub/rhythm-semiclassical.jpg', body: 'Thumri, dadra and bhajan: classical, with feeling first.', tagline: 'Where classical meets feeling', what: 'Thumri, dadra, bhajan and more: the softer rooms of the classical house, where emotion leads and technique follows.', a: ['Emotion first, technique second', 'You sing along before you analyse', 'Pure classical feels heavy, but close', 'You love an evening mehfil'], b: ['Thumri evenings', 'Bhajan circles', 'Gentle technique clinics', 'Song-story sessions'], note: 'Great for relaxation and emotion.' },
        { name: 'Ghazal', icon: 'sparkle', image: '/img/sub/rhythm-ghazal.jpg', body: 'Poetry, melody and deep feeling that touch the soul.', tagline: 'Poetry, set to melody', what: 'Couplets that carry whole lives: poetry, melody and deep feeling, sung slowly enough to land.', a: ['Urdu poetry stops you mid-scroll', 'Reflection suits your evenings', 'You collect couplets like others collect coins', 'You want words and music in equal measure'], b: ['Ghazal appreciation circles', 'Diction and delivery clinics', 'Poet deep-dives', 'Open-mic mehfils'], note: 'Great for reflection. Bring a shayr you love.' },
        { name: 'Sufi & Qawwali', icon: 'heart', image: '/img/sub/rhythm-sufi.jpg', body: 'Soul-stirring music that connects you to something higher.', tagline: 'Music as prayer', what: 'Soul-stirring music that connects you to something higher, one repetition at a time.', a: ['Energy and spirituality, together', 'A dargah qawwali once moved you', 'Repetition lifts you, never bores you', 'You clap on the offbeat, happily'], b: ['Qawwali listening nights', 'Zikr and rhythm circles', 'Kalam study', 'Live mehfils'], note: 'Great for spirituality and energy.' },
        { name: 'Devotional & Bhakti', icon: 'leaf', image: '/img/sub/rhythm-bhakti.jpg', body: 'Songs of devotion, across traditions and languages.', tagline: 'Songs that uplift', what: 'Songs of devotion across traditions and languages that lift the heart, mornings especially.', a: ['You sing to feel close to something', 'Aartis and abhangs feel like home', 'Language is no barrier for you', 'Your mornings need music'], b: ['Morning bhakti circles', 'Regional song exchanges', 'Festival choirs', 'Lyric meanings, explained'], note: 'Great for devotion and positivity. All languages welcome.' },
      ],
    },
    hue: communityHues.rose,
    slug: 'rhythm',
    name: 'Rhythm',
    topic: 'Music',
    members: '9,800',
    titleA: 'practice,',
    titleWord: 'heard',
    intro:
      'Record your riyaaz, get a real video reply from your LifeCrafter. Solitary practice becomes a relationship: vocals, guitar, tabla and songwriting, guided by musicians who listen back.',
    paths: ['Vocals', 'Guitar', 'Tabla & percussion', 'Songwriting'],
    image: '/img/rhythm.jpg',
    gallery: ['/img/gen/hero-riyaaz.jpg', '/img/gen/hero-kirtan.jpg', '/img/gen/card-rhythm.jpg'],
    events: [
      { day: 'Sun', time: '11:00', kicker: 'Online · live on LifeCraft4U', title: 'Riyaaz Circle: Ragas', blurb: 'Sing together, get gentle feedback from Vikram and the circle.' },
      { day: 'Tue', time: '7:30', kicker: 'Online · live on LifeCraft4U', title: 'Guitar for Returning Hands', blurb: 'Chords you half remember, patiently rebuilt.' },
      { day: 'Sat', time: '5:00', kicker: 'In person · Mumbai', title: 'Evening Raag Circle', blurb: 'An unplugged evening of ragas and chai.' },
    ],
    voice: { quote: 'I rediscovered singing at 54, and found people who actually listen back.', who: 'Meera, 54' },
    host: { name: 'Vikram Menon', craft: 'Hindustani vocals. Trains returning singers with weekly video replies.', years: '22 yrs', photo: '/img/portrait-3.jpg' },
  },
}

/* ---- LifeCrafter membership tiers (placeholder pricing) --------------- */
export type CrafterTier = { name: string; price: string; per?: string; line: string; features: string[]; featured?: boolean }
export const crafterTiers: CrafterTier[] = [
  {
    name: 'Premium',
    price: '₹499',
    per: 'per month',
    line: 'For a steady weekly rhythm.',
    features: ['Unlimited events & workshops', 'One daily program', 'RSVP & payments handled', 'Reduced platform fee'],
    featured: true,
  },
  {
    name: 'Elite',
    price: '₹1,499',
    per: 'per month',
    line: 'Your practice, full time.',
    features: ['Everything in Premium', '1-1 consultation tools', 'Featured in your community', 'Lowest platform fee'],
  },
  {
    name: 'Mentors',
    price: 'On request',
    line: 'One-on-one mentorship, priced per mentor.',
    features: ['1-1 consultation tools', 'Set your own rates', 'Featured mentor profile', 'A human account manager'],
  },
]

/* ---- Cosmos people: real astrologers with rich card + profile modal ---- */
export type CosmosMentor = {
  name: string
  /** short name shown on the mentor card (first name only); modal uses full `name` */
  displayName?: string
  /** card subtitle / area of expertise, e.g. 'Astrology Expert' */
  focus: string
  years: string
  photo: string
  /** true while the portrait is still a placeholder pending a real photo */
  photoPending?: boolean
  /** short card description */
  short?: string
  /** specialization chips shown on the card */
  specialties?: string[]
  /** full English bio paragraphs shown in the profile modal */
  about?: string[]
  /** expertise tags shown at the bottom of the modal */
  expertise?: string[]
  /** Hindi bio paragraphs; when present the modal shows an EN/हिं toggle */
  aboutHi?: string[]
  /** Hindi expertise tags, shown when the bio is switched to Hindi */
  expertiseHi?: string[]
  /** Hindi name, focus and experience — the full modal localises when toggled */
  nameHi?: string
  focusHi?: string
  yearsHi?: string
}
export const cosmosMentors: CosmosMentor[] = [
  {
    name: 'Naresh Kumar Sharma',
    displayName: 'Shri Naresh',
    focus: 'Vedic Astrologer',
    years: '35+ Years',
    photo: '/img/astro-naresh.jpg',
    short: 'Vedic astrologer and author blending scriptural knowledge with practical life prediction.',
    specialties: ['Vedic Astrology', 'Numerology', 'Dasha & Gochar', 'Yogas & Doshas'],
    about: [
      'Naresh Kumar Sharma is an experienced astrologer and author with a deep study of Vedic astrology, numerology and Indian spiritual traditions. He specialises in birth chart analysis, Dasha and Gochar, Yogas and Doshas, and astrological guidance related to marriage, career, health and finances.',
      'His approach combines scriptural knowledge with practical experience, presenting complex astrological concepts in a simple and accessible manner.',
    ],
    aboutHi: [
      'नरेश कुमार शर्मा एक अनुभवी ज्योतिषाचार्य और लेखक हैं, जिन्हें वैदिक ज्योतिष, अंक ज्योतिष और भारतीय आध्यात्मिक परंपराओं का गहन अध्ययन है। वे जन्म कुंडली विश्लेषण, दशा एवं गोचर, योग एवं दोष तथा विवाह, करियर, स्वास्थ्य और वित्त से जुड़े विषयों पर ज्योतिषीय मार्गदर्शन प्रदान करते हैं।',
      'उनका दृष्टिकोण शास्त्रीय ज्ञान और व्यावहारिक अनुभव का संतुलित मेल है और वे जटिल ज्योतिषीय विषयों को सरल एवं सहज भाषा में प्रस्तुत करते हैं।',
    ],
    expertise: ['Vedic Astrology', 'Numerology', 'Birth Chart Analysis', 'Dasha & Gochar', 'Yogas & Doshas', 'Marriage & Career Guidance'],
    expertiseHi: ['वैदिक ज्योतिष', 'अंक ज्योतिष', 'जन्म कुंडली विश्लेषण', 'दशा एवं गोचर', 'योग एवं दोष', 'विवाह एवं करियर मार्गदर्शन'],
    nameHi: 'नरेश कुमार शर्मा',
    focusHi: 'वैदिक ज्योतिषी',
  },
  {
    name: 'Vivek Kush',
    displayName: 'Shri Vivek',
    focus: 'Vedic Astrologer',
    years: '35+ Years',
    photo: '/img/astro-vivek.jpg',
    short: 'Vedic astrologer known for accurate horoscope analysis, practical remedies and compassionate guidance.',
    specialties: ['Horoscope Analysis', 'Gemstone Consultation', 'Remedial Guidance', 'Vastu'],
    about: [
      'Vivek Kush is a learned astrologer with 37 years of dedicated experience in Vedic astrology. He has completed formal education in Phalit Jyotish from a state government university and combines classical scriptural knowledge with practical life prediction.',
      'He has guided thousands of families across India on matters related to career, marriage, health, finance and Vastu through horoscope analysis, gemstone consultation and remedial guidance. His approach combines traditional wisdom with clarity, honesty and compassion.',
    ],
    aboutHi: [
      'विवेक कुश एक विद्वान ज्योतिषी हैं, जिन्हें वैदिक ज्योतिष के क्षेत्र में 37 वर्षों का समर्पित अनुभव है। उन्होंने राज्य सरकार के विश्वविद्यालय से फलित ज्योतिष की औपचारिक शिक्षा प्राप्त की है और शास्त्रीय ज्ञान को व्यावहारिक जीवन भविष्यवाणी के साथ जोड़कर कार्य करते हैं।',
      'उन्होंने भारत भर में हजारों परिवारों को करियर, विवाह, स्वास्थ्य, वित्त और वास्तु से जुड़े विषयों में कुंडली विश्लेषण, रत्न परामर्श और ज्योतिषीय उपायों के माध्यम से मार्गदर्शन दिया है। उनका दृष्टिकोण पारंपरिक ज्ञान, स्पष्टता, ईमानदारी और संवेदनशीलता पर आधारित है।',
    ],
    expertise: ['Vedic Astrology', 'Horoscope Analysis', 'Career', 'Marriage', 'Health', 'Finance', 'Vastu', 'Gemstone Consultation'],
    expertiseHi: ['वैदिक ज्योतिष', 'कुंडली विश्लेषण', 'करियर', 'विवाह', 'स्वास्थ्य', 'वित्त', 'वास्तु', 'रत्न परामर्श'],
    nameHi: 'विवेक कुश',
    focusHi: 'वैदिक ज्योतिषी',
    yearsHi: '35+ वर्ष',
  },
  {
    name: 'Nukala Suryanarayana',
    displayName: 'Shri Suryanarayana',
    focus: 'Astro-Vastu Consultant',
    years: '35+ Years',
    photo: '/img/astro-nookala.jpg',
    short: 'Astro-Vastu consultant, advisory expert and media analyst blending traditional astrology with a contemporary view.',
    specialties: ['Astro-Vastu', 'Horoscope Analysis', 'Vastu Assessment', 'Business Astrology'],
    about: [
      'Nukala Suryanarayana is an experienced Astro-Vastu Consultant, Advisory Expert and Media Analyst with 35+ years of expertise. He specialises in advanced horoscope analysis, planetary alignments, Vastu-driven structural assessments and practical applications of astrology in business and daily life.',
      'He has shared his expertise through television, radio and print media, including astrology programs on ETV, Sakshi and Big FM Radio. His approach combines traditional astrological knowledge with contemporary perspectives, focusing on accuracy, clarity and practical guidance.',
    ],
    aboutHi: [
      'नुकाला सूर्यनारायण एक अनुभवी एस्ट्रो-वास्तु कंसल्टेंट, एडवाइजरी एक्सपर्ट और मीडिया एनालिस्ट हैं, जिन्हें 35+ वर्षों का अनुभव है। वे उन्नत कुंडली विश्लेषण, ग्रहों की स्थिति, वास्तु आधारित संरचनात्मक मूल्यांकन तथा व्यवसाय और दैनिक जीवन में ज्योतिष के व्यावहारिक उपयोग में विशेषज्ञता रखते हैं।',
      'उन्होंने टेलीविजन, रेडियो और प्रिंट मीडिया के माध्यम से ज्योतिष संबंधी अपने ज्ञान को व्यापक दर्शकों तक पहुँचाया है, जिसमें ETV, Sakshi और Big FM Radio पर उनके कार्यक्रम शामिल हैं। उनका दृष्टिकोण पारंपरिक ज्योतिषीय ज्ञान को आधुनिक सोच के साथ जोड़ते हुए स्पष्ट, सटीक और व्यावहारिक मार्गदर्शन पर केंद्रित है।',
    ],
    expertise: ['Astro-Vastu', 'Horoscope Analysis', 'Planetary Alignments', 'Vastu Assessment', 'Business Astrology', 'Strategic Advisory'],
    expertiseHi: ['एस्ट्रो-वास्तु', 'कुंडली विश्लेषण', 'ग्रहों की स्थिति', 'वास्तु मूल्यांकन', 'व्यावसायिक ज्योतिष', 'रणनीतिक परामर्श'],
    nameHi: 'नुकाला सूर्यनारायण',
    focusHi: 'एस्ट्रो-वास्तु कंसल्टेंट',
    yearsHi: '35+ वर्ष',
  },
  {
    name: 'Acharya Poonam Gaur',
    displayName: 'Acharya Poonam',
    focus: 'Astrology & Healing Expert',
    years: '15+ Years',
    photo: '/img/astro-poonam.jpg',
    short: 'Expert in astrology, predictive analysis and spiritual guidance, with a practical, modern approach.',
    specialties: ['Birth Chart Analysis', 'Varshphal', 'Planetary Transits', 'Numerology', 'Panchang'],
    about: [
      'Acharya Poonam Gaur is an experienced astrologer, author and guide with 15+ years of practical experience. She specialises in birth chart analysis, Varshphal, planetary transits, numerology and Panchang.',
      'Known for explaining complex astrological concepts in simple language, she combines traditional knowledge with a modern, practical approach to help people make informed decisions and navigate life with greater clarity and confidence.',
    ],
    aboutHi: [
      'आचार्य पूनम गौड़ एक अनुभवी ज्योतिषाचार्य, लेखिका और मार्गदर्शक हैं, जिन्हें ज्योतिष के क्षेत्र में 15+ वर्षों का व्यावहारिक अनुभव है। वे जन्म कुंडली विश्लेषण, वर्षफल, ग्रह गोचर, अंक ज्योतिष और पंचांग में विशेषज्ञता रखती हैं।',
      'जटिल ज्योतिषीय विषयों को सरल भाषा में समझाने के लिए जानी जाने वाली आचार्य पूनम गौड़ पारंपरिक ज्योतिष ज्ञान को आधुनिक और व्यावहारिक दृष्टिकोण के साथ प्रस्तुत करती हैं, जिससे लोग जीवन के महत्वपूर्ण निर्णय अधिक स्पष्टता और आत्मविश्वास के साथ ले सकें।',
    ],
    expertise: ['Birth Chart Analysis', 'Varshphal', 'Planetary Transits', 'Numerology', 'Panchang', 'Healing', 'Spiritual Guidance'],
    expertiseHi: ['जन्म कुंडली विश्लेषण', 'वर्षफल', 'ग्रहों का गोचर', 'अंक ज्योतिष', 'पंचांग', 'हीलिंग', 'आध्यात्मिक मार्गदर्शन'],
    nameHi: 'आचार्य पूनम गौड़',
    focusHi: 'ज्योतिष एवं हीलिंग विशेषज्ञ',
    yearsHi: '15+ वर्ष',
  },
  {
    name: 'Alok Gautam',
    displayName: 'Shri Alok',
    focus: 'KP Astrology & Modern Vaastu',
    years: '25+ Years',
    photo: '/img/astro-alok.jpg',
    short: 'KP Astrologer and Modern Vaastu Consultant known for practical, non-demolition Vaastu solutions.',
    specialties: ['KP Astrology', 'Modern Vaastu', 'Vaastu Dosh Correction', 'Astro-Vaastu Guidance'],
    about: [
      'Alok Gautam is an experienced KP Astrologer and Modern Vaastu Consultant with 25+ years of professional practice. He specialises in KP Astrology, Modern Vaastu, Vaastu Dosh Analysis and non-demolition Vaastu corrections for residential, commercial, industrial and institutional spaces.',
      'He has conducted 3,000+ Vaastu site visits and trained approximately 8,000 students in KP Astrology and Modern Vaastu. His approach focuses on identifying Vaastu imbalances and providing practical solutions to support harmony, health, relationships, career and business growth.',
    ],
    aboutHi: [
      'आलोक गौतम एक अनुभवी केपी ज्योतिषी और मॉडर्न वास्तु कंसल्टेंट हैं, जिन्हें 25+ वर्षों का पेशेवर अनुभव है। वे केपी ज्योतिष, मॉडर्न वास्तु, वास्तु दोष विश्लेषण और बिना तोड़फोड़ किए वास्तु सुधार में विशेषज्ञता रखते हैं।',
      'उन्होंने आवासीय, व्यावसायिक, औद्योगिक और संस्थागत स्थानों के लिए 3,000+ वास्तु साइट विज़िट की हैं तथा लगभग 8,000 विद्यार्थियों को केपी ज्योतिष और मॉडर्न वास्तु का प्रशिक्षण दिया है। उनका दृष्टिकोण वास्तु असंतुलन की पहचान कर व्यावहारिक समाधान प्रदान करने पर केंद्रित है, जिससे स्वास्थ्य, संबंधों, करियर और व्यवसाय में सकारात्मक वातावरण विकसित करने में सहायता मिल सके।',
    ],
    expertise: ['KP Astrology', 'Modern Vaastu', 'Vaastu Dosh Analysis', 'Non-Demolition Corrections', 'Residential & Commercial Vaastu', 'Industrial Vaastu', 'Astro-Vaastu Guidance'],
    expertiseHi: ['केपी ज्योतिष', 'मॉडर्न वास्तु', 'वास्तु दोष विश्लेषण', 'बिना तोड़फोड़ सुधार', 'आवासीय एवं व्यावसायिक वास्तु', 'औद्योगिक वास्तु', 'एस्ट्रो-वास्तु मार्गदर्शन'],
    nameHi: 'आलोक गौतम',
    focusHi: 'केपी ज्योतिष एवं मॉडर्न वास्तु',
    yearsHi: '25+ वर्ष',
  },
  {
    name: 'Dr. Minaakshi Rathour',
    displayName: 'Dr. Minaakshi',
    focus: 'Tarot & Intuitive Coaching',
    years: '17+ Years',
    photo: '/img/astro-minaakshi.jpg',
    short: 'Award-winning Celebrity Tarot Expert and Intuitive Transformation Coach helping people find clarity, healing and growth.',
    specialties: ['Tarot Reading', 'Numerology', 'Akashic Records', 'Emotional Transformation', 'Success Coaching'],
    about: [
      'Dr. Minaakshi Rathour is an award-winning Celebrity Tarot Expert, Intuitive Life & Emotional Wellness Coach, Numerologist, Akashic Records Practitioner, author and spiritual mentor with 17+ years of experience. She specialises in Tarot Reading, Numerology, Akashic Records Healing, Relationship Guidance, Emotional Transformation, Personal Growth and Success Coaching.',
      'Her approach combines ancient intuitive wisdom with practical coaching to help people find clarity, healing and momentum toward personal and professional growth.',
    ],
    aboutHi: [
      'डॉ. मीनाक्षी राठौर एक पुरस्कार विजेता सेलिब्रिटी टैरो विशेषज्ञ, इंट्यूटिव लाइफ एवं इमोशनल वेलनेस कोच, अंक ज्योतिष विशेषज्ञ, आकाशिक रिकॉर्ड्स प्रैक्टिशनर, लेखिका और आध्यात्मिक मार्गदर्शक हैं, जिन्हें 17+ वर्षों का अनुभव है। वे टैरो रीडिंग, अंक ज्योतिष, आकाशिक रिकॉर्ड्स हीलिंग, रिलेशनशिप गाइडेंस, भावनात्मक परिवर्तन, व्यक्तिगत विकास और सफलता मार्गदर्शन में विशेषज्ञता रखती हैं।',
      'उनका दृष्टिकोण प्राचीन आध्यात्मिक ज्ञान को आधुनिक लाइफ कोचिंग के साथ जोड़ता है, जिससे लोगों को जीवन में स्पष्टता, आत्मविश्वास और सकारात्मक दिशा प्राप्त करने में सहायता मिलती है।',
    ],
    expertise: ['Tarot Reading', 'Numerology', 'Akashic Records', 'Relationship Guidance', 'Emotional Transformation', 'Personal Growth', 'Success Coaching'],
    expertiseHi: ['टैरो रीडिंग', 'अंक ज्योतिष', 'आकाशिक रिकॉर्ड्स', 'रिलेशनशिप गाइडेंस', 'भावनात्मक परिवर्तन', 'व्यक्तिगत विकास', 'सफलता मार्गदर्शन'],
    nameHi: 'डॉ. मीनाक्षी राठौर',
    focusHi: 'टैरो एवं इंट्यूटिव कोचिंग',
    yearsHi: '17+ वर्ष',
  },
  {
    name: 'H.S. Rawat',
    displayName: 'H.S. Rawat',
    focus: 'Astrology & Spiritual Guidance',
    years: '45+ Years',
    photo: '/img/astro-hsrawat.jpg',
    short: 'Renowned spiritual leader, astrologer, author, and TV personality with extensive expertise in astrology and spirituality, combining traditional Jyotish wisdom with practical guidance.',
    specialties: ['Predictive Astrology', 'Horoscope Analysis', 'Panchang', 'Vastu Shastra', 'Numerology', 'Spiritual Guidance'],
    about: [
      'H.S. Rawat is a renowned spiritual leader, astrologer, author, and TV personality with extensive experience in astrology and spiritual guidance. He is the Director of Maharshi Parashar Jyotish Vidyalaya, with branches across Delhi, Mumbai, Punjab, and Noida.',
      'An author of 15+ books and recipient of numerous national and international honors, he specializes in predictive astrology, horoscope analysis, Panchang, Vastu Shastra, numerology, and spiritual guidance. Through television, books, and digital platforms, he has dedicated his work to making astrology accessible and practical for modern audiences.',
    ],
    expertise: ['Predictive Astrology', 'Horoscope Analysis', 'Panchang', 'Vastu Shastra', 'Numerology', 'Spiritual Guidance'],
    aboutHi: [
      'H.S. रावत एक जाने-माने आध्यात्मिक गुरु, ज्योतिषी, लेखक और टीवी पर्सनैलिटी हैं, जिन्हें ज्योतिष और आध्यात्मिक मार्गदर्शन का लंबा अनुभव है। वह ‘महर्षि पराशर ज्योतिष विद्यालय’ के डायरेक्टर हैं, जिसकी शाखाएँ दिल्ली, मुंबई, पंजाब और नोएडा में हैं।',
      '15 से ज़्यादा किताबों के लेखक और कई राष्ट्रीय व अंतर्राष्ट्रीय सम्मान पाने वाले H.S. रावत भविष्य बताने वाले ज्योतिष (प्रेडिक्टिव एस्ट्रोलॉजी), कुंडली विश्लेषण, पंचांग, वास्तु शास्त्र, अंकशास्त्र और आध्यात्मिक मार्गदर्शन में माहिर हैं। उन्होंने टेलीविज़न, किताबों और डिजिटल प्लेटफ़ॉर्म के ज़रिए ज्योतिष को आज के लोगों के लिए आसान और व्यावहारिक बनाने का काम किया है।',
    ],
    expertiseHi: ['भविष्य बताने वाला ज्योतिष', 'कुंडली विश्लेषण', 'पंचांग', 'वास्तु शास्त्र', 'अंकशास्त्र', 'आध्यात्मिक मार्गदर्शन'],
    nameHi: 'एच.एस. रावत',
    focusHi: 'ज्योतिष एवं आध्यात्मिक मार्गदर्शन',
    yearsHi: '45+ वर्ष',
  },
  {
    name: 'Dr. Arun Bansal',
    displayName: 'Dr. Arun Bansal',
    focus: 'Astrology & Vastu',
    years: '45+ Years',
    photo: '/img/astro-arun.jpg',
    short: 'Renowned astrologer and Vastu expert with 45+ years of experience, known for combining traditional Vedic wisdom with modern approaches to astrological practice.',
    specialties: ['Vedic Astrology', 'Vastu Shastra', 'Predictive Astrology', 'Panchang', 'Muhurta', 'Numerology'],
    about: [
      'Dr. Arun Bansal is a renowned astrologer and Vastu expert with over 45 years of experience in astrology. He has authored 100+ books on Astrology and Vastu and has made significant contributions to the study and practice of modern Jyotish.',
      'With an M.Sc. in Physics, an M.Phil. in Computer Science, and a Ph.D. in Astrology, Dr. Bansal brings together deep traditional knowledge with a strong academic and analytical approach. His expertise covers Vedic Astrology, Vastu Shastra, Panchang, Muhurta, Numerology, and predictive astrology.',
    ],
    expertise: ['Vedic Astrology', 'Vastu Shastra', 'Predictive Astrology', 'Panchang', 'Muhurta', 'Numerology'],
    aboutHi: [
      'डॉ. अरुण बंसल एक जाने-माने ज्योतिषी और वास्तु विशेषज्ञ हैं, जिन्हें ज्योतिष के क्षेत्र में 45 से ज़्यादा वर्षों का अनुभव है। उन्होंने ज्योतिष और वास्तु पर 100 से ज़्यादा किताबें लिखी हैं और आधुनिक ज्योतिष के अध्ययन और अभ्यास में महत्वपूर्ण योगदान दिया है।',
      'फिजिक्स में M.Sc., कंप्यूटर साइंस में M.Phil. और ज्योतिष में Ph.D. करने वाले डॉ. बंसल, पारंपरिक ज्ञान को एक मज़बूत एकेडमिक और एनालिटिकल नज़रिए के साथ जोड़ते हैं। उनकी विशेषज्ञता वैदिक ज्योतिष, वास्तु शास्त्र, पंचांग, मुहूर्त, अंक ज्योतिष और भविष्य बताने वाले ज्योतिष (प्रेडिक्टिव एस्ट्रोलॉजी) में है।',
    ],
    expertiseHi: ['वैदिक ज्योतिष', 'वास्तु शास्त्र', 'भविष्य बताने वाला ज्योतिष', 'पंचांग', 'मुहूर्त', 'अंक ज्योतिष'],
    nameHi: 'अरुण बंसल',
    focusHi: 'ज्योतिष एवं वास्तु',
    yearsHi: '45+ वर्ष',
  },
]

/* ---- Cosmos marketplace: courses, books, tools and retreats ----------- */
export const cosmosMarketplace: MarketplaceData = {
  kicker: 'Alliances & partners',
  title: 'The Cosmos marketplace.',
  intro: 'Courses, books, tools and retreats from partner schools and trusted sellers.',
  categories: ['Partner courses', 'Books & almanacs', 'Tools', 'Retreats'],
  items: [
    { kind: 'Course', title: 'Vedic Foundations', partner: 'Jyotish Vidya Academy', blurb: 'Charts, houses and planets from first principles, live twice a week.', meta: '6 weeks · Beginner', price: '₹4,999' },
    { kind: 'Course', title: 'Tarot, Level One', partner: 'The Arcana School', blurb: 'The full deck, spread by spread, with weekly practice partners.', meta: '4 weeks · Beginner', price: '₹2,999' },
    { kind: 'Course', title: 'KP Timing Techniques', partner: 'Nakshatra Institute', blurb: 'Precise prediction methods for students who know their basics.', meta: '8 weeks · Intermediate', price: '₹7,499' },
    { kind: 'Books', title: 'Panchang Almanac 2026', partner: 'Jyotish Prakashan', blurb: 'The year, day by day: tithis, nakshatras and good hours.', meta: 'Annual edition', price: '₹850' },
    { kind: 'Tools', title: 'Chart-Drawing Starter Kit', partner: 'Nakshatra Tools', blurb: 'Blank charts, stencils and a very good pen.', meta: 'Ships across India', price: '₹1,400' },
    { kind: 'Retreat', title: 'Sky & Silence Weekend', partner: 'Himalayan Observatory Stays', blurb: 'Two nights under a dark sky with a telescope and a chart.', meta: '2 nights · Bir', price: '₹9,500' },
  ],
  note: 'Run a school, press or observatory? Partner tiers open on the LifeCrafter Program.',
}

/* ---- Cosmos courses (superseded by cosmosMarketplace) ------------------ */
export type CosmosCourse = { title: string; partner: string; length: string; level: string; price: string; blurb: string }
export const cosmosCourses: CosmosCourse[] = [
  { title: 'Vedic Foundations', partner: 'Jyotish Vidya Academy', length: '6 weeks', level: 'Beginner', price: '₹4,999', blurb: 'Charts, houses and planets from first principles, live twice a week.' },
  { title: 'Tarot, Level One', partner: 'The Arcana School', length: '4 weeks', level: 'Beginner', price: '₹2,999', blurb: 'The full deck, spread by spread, with weekly practice partners.' },
  { title: 'KP Timing Techniques', partner: 'Nakshatra Institute', length: '8 weeks', level: 'Intermediate', price: '₹7,499', blurb: 'Precise prediction methods for students who know their basics.' },
]

/* ---- Cosmos sub-genre deep dives (adapted from the category board) ---- */
export type SubgenreDetail = {
  tagline: string
  what: string
  consult: string[]
  gain: string[]
  note: string
}

export const cosmosSubgenres: Record<string, SubgenreDetail> = {
  Vedic: {
    tagline: 'The long view',
    what: 'The classical Indian system: your birth chart read through rashis, houses and dashas, giving a long view of the seasons of your life.',
    consult: ['You want a full map of the year ahead', 'A big decision needs a long view', 'You are curious about your dashas and transits', 'You want one practice to grow old with'],
    gain: ['A guided read of your birth chart', 'Season-by-season outlook', 'Steady monthly check-ins', 'Language for what you are living through'],
    note: 'Bring your birth date, time and place; the more precise, the sharper the chart.',
  },
  KP: {
    tagline: 'Event timing',
    what: 'A precise, time-tested refinement of Vedic astrology built for event timing: clear answers to specific questions, down to the month.',
    consult: ['When will it happen: job, home, travel', 'You need a yes-or-no read on a decision', 'Timing matters more than temperament', 'You like your answers exact'],
    gain: ['Accurate event timing', 'Confidence in decision making', 'Clarity on important life events', 'Better planning for the future'],
    note: 'KP is known for precision and a near-scientific approach to timing life events.',
  },
  Nadi: {
    tagline: 'Destiny, written on leaves',
    what: 'An ancient practice based on palm-leaf manuscripts attributed to the Siddha rishis, believed to hold remarkably specific readings of a life.',
    consult: ['You want an extremely specific reading', 'Questions of karma and past patterns', 'A turning point needs a deeper look', 'Curiosity about the tradition itself'],
    gain: ['Detailed event predictions', 'Karmic insight', 'Spiritual understanding', 'Destiny guidance'],
    note: 'Nadi readings do not require your time of birth; a thumb impression and your name open the leaf.',
  },
  'Lal Kitab': {
    tagline: 'The red book',
    what: 'The folk classic: plain-spoken readings and small, practical remedies you can actually do.',
    consult: ['You want simple, doable remedies', 'A recurring pattern will not budge', 'You prefer plain language to jargon', 'Questions of home and family'],
    gain: ['Simple remedies, kindly given', 'A practical read of your chart', 'Small rituals for stuck seasons', 'Folk wisdom, held lightly'],
    note: 'Remedies are gentle and inexpensive by design: feed something, gift something, mend something.',
  },
  Numerology: {
    tagline: 'Patterns in the numbers',
    what: 'The study of numbers in your dates and names, and the patterns they suggest for temperament and timing.',
    consult: ['Naming a child, a company, a project', 'Your dates keep rhyming and you wonder why', 'You want a lightweight practice beside the chart', 'You are numerically curious'],
    gain: ['Your core numbers, explained', 'Name and date harmonics', 'A yearly number outlook', 'A playful daily practice'],
    note: 'Numerology pairs well with a chart reading; many members keep both.',
  },
  Vastu: {
    tagline: 'Harmony at home',
    what: 'The science of space: how homes and workplaces sit with the directions and elements, and the small changes that make them feel right.',
    consult: ['Setting up a new home or office', 'A room that never quite feels right', 'Renovation or a big purchase decision', 'Balancing work and rest under one roof'],
    gain: ['A practical read of your space', 'Small changes, big ease', 'Element-wise balance', 'Confidence in your choices'],
    note: 'Most guidance works with what you already have: no demolition required.',
  },
  Tarot: {
    tagline: 'Symbol and image',
    what: 'Guidance through a spread of cards: a mirror for the question you brought, read with you, not at you.',
    consult: ['You are circling a decision', 'You want a conversation, not a verdict', 'Something needs naming before it needs solving', 'You are new, and curious'],
    gain: ['A guided spread, read with you', 'Sharper questions', 'A gentler view of your options', 'Your own intuition, warmed up'],
    note: 'The cards start conversations, never sentences. Nothing here decides for you.',
  },
}

/* ---- Legal pages: sourced from the client's existing policies -------- */
export type LegalBlock =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'sub'; label: string; text: string }
export type LegalSection = { n: string; heading: string; blocks: LegalBlock[] }
export type LegalPage = {
  slug: string
  kicker: string
  title: string
  updated: string
  intro: string
  sections: LegalSection[]
}

export const legalPages: Record<string, LegalPage> = {
  privacy: {
    slug: 'privacy-policy',
    kicker: 'Legal',
    title: 'Privacy Policy',
    updated: 'Last updated 4 September 2026',
    intro:
      'At LifeCraft4U, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our services.',
    sections: [
      {
        n: '01',
        heading: 'Information We Collect',
        blocks: [
          { kind: 'p', text: 'We collect information that you provide directly to us when you register for an account, subscribe to our newsletter, or contact us for support. This may include:' },
          { kind: 'list', items: [
            'Personal identifiers such as your full name and email address.',
            'Demographic information including age, gender, and professional interests.',
            'Account credentials and security information.',
          ] },
        ],
      },
      {
        n: '02',
        heading: 'Purpose of Data Collection',
        blocks: [
          { kind: 'p', text: 'We process your personal data for the following legitimate business purposes:' },
          { kind: 'sub', label: 'Service Delivery', text: 'To provide, maintain, and improve our services and personalized experiences.' },
          { kind: 'sub', label: 'Communication', text: 'To send technical notices, updates, security alerts, and support messages.' },
        ],
      },
      {
        n: '03',
        heading: 'Payment Data',
        blocks: [
          { kind: 'p', text: 'We do not store your credit card details on our servers. All payments are processed by secure third-party payment processors (e.g., RazorPay, Phone Pe, CashFree) compliant with PCI-DSS standards.' },
        ],
      },
      {
        n: '04',
        heading: 'Data Security',
        blocks: [
          { kind: 'p', text: 'We implement industry-standard administrative, technical, and physical security measures designed to protect your personal information from unauthorized access, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.' },
        ],
      },
      {
        n: '05',
        heading: 'User Rights',
        blocks: [
          { kind: 'p', text: 'Depending on your location, you may have the following rights regarding your personal data:' },
          { kind: 'list', items: [
            'The right to access your data.',
            'The right to correct inaccurate information.',
            'The right to request deletion of your data.',
          ] },
        ],
      },
      {
        n: '06',
        heading: 'Third-Party Disclosure',
        blocks: [
          { kind: 'p', text: 'We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties except to provide the service requested, comply with the law, or protect our rights.' },
        ],
      },
    ],
  },
  terms: {
    slug: 'terms',
    kicker: 'Legal',
    title: 'Terms of Use',
    updated: 'Last updated 4 September 2026',
    intro:
      'Please read these terms and conditions carefully before using LifeCraft4U. By using this website, you agree to be bound by these terms.',
    sections: [
      {
        n: '01',
        heading: 'Acceptance of Terms',
        blocks: [
          { kind: 'p', text: 'By accessing or using the LifeCraft4U platform, you agree to be bound by these Terms of Use and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site. The materials contained in this website are protected by applicable copyright and trademark law.' },
          { kind: 'p', text: 'We reserve the right to modify these terms at any time. Your continued use of the platform after changes have been posted will constitute your acceptance of those changes.' },
        ],
      },
      {
        n: '02',
        heading: 'Eligibility',
        blocks: [
          { kind: 'p', text: 'To use LifeCraft4U, you must be at least 18 years of age and possess the legal authority to enter into these Terms. By using the platform, you represent and warrant that you meet all of these requirements. If you do not meet all of these requirements, you must not access or use the platform.' },
        ],
      },
      {
        n: '03',
        heading: 'Platform Role',
        blocks: [
          { kind: 'p', text: 'LifeCraft4U provides a digital community platform designed to help people find communities and daily practices built around their interests. We act solely as a technology service provider.' },
          { kind: 'p', text: 'We do not provide professional legal, financial, or medical advice. Users should consult with qualified professionals for specific advisory needs.' },
        ],
      },
      {
        n: '04',
        heading: 'User Responsibilities',
        blocks: [
          { kind: 'sub', label: 'Account Security', text: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.' },
          { kind: 'sub', label: 'Content Accuracy', text: 'You agree to provide accurate, current, and complete information and to update such information to keep it accurate and complete.' },
        ],
      },
    ],
  },
  instructor: {
    slug: 'instructor-policy',
    kicker: 'Legal',
    title: 'Instructor & Facilitator Policy',
    updated: 'Last updated 4 September 2026',
    intro:
      'Guidelines and standards for teaching on LifeCraft4U. We are committed to providing the highest quality experience for our community members.',
    sections: [
      {
        n: '01',
        heading: 'Instructor Onboarding',
        blocks: [
          { kind: 'p', text: 'To maintain high standards, all facilitators must undergo an internal review and verification process. This ensures that our members receive guidance from qualified and trustworthy individuals.' },
          { kind: 'sub', label: 'Credentials Check', text: 'Verification of professional certifications, academic degrees, and relevant industry experience.' },
          { kind: 'sub', label: 'Identity Verification', text: "Submission of government-issued identification to confirm the instructor's identity." },
          { kind: 'sub', label: 'Interview Process', text: 'A structured conversation with our team to assess communication skills and pedagogical approach.' },
        ],
      },
      {
        n: '02',
        heading: 'Instructor Responsibilities',
        blocks: [
          { kind: 'p', text: 'Instructors are the face of LifeCraft4U. We expect a commitment to excellence in the following areas:' },
          { kind: 'sub', label: 'Authenticity', text: 'Content must be original or appropriately licensed, providing genuine value to our users.' },
          { kind: 'sub', label: 'Professional Conduct', text: 'Maintain a professional demeanor in all interactions, including live sessions, forums, and private messaging.' },
          { kind: 'sub', label: 'Quality Delivery', text: 'Ensure video, audio, and written materials meet LifeCraft4U technical and editorial quality standards.' },
        ],
      },
      {
        n: '03',
        heading: 'Content Ownership',
        blocks: [
          { kind: 'p', text: 'We value intellectual property. Our ownership model is designed to be fair to creators:' },
          { kind: 'sub', label: 'Instructor Ownership', text: 'You retain all ownership rights to the content you create and upload to the platform.' },
          { kind: 'sub', label: 'Usage License', text: 'By uploading content, you grant LifeCraft4U a non-exclusive, worldwide, royalty-free license to host, display, and promote your content.' },
          { kind: 'sub', label: 'Marketing', text: 'We may use snippets of your content for promotional purposes across our social media and marketing channels.' },
        ],
      },
      {
        n: '04',
        heading: 'Policy Enforcement',
        blocks: [
          { kind: 'p', text: 'Failure to comply with these guidelines or the broader Terms of Service may result in administrative action. LifeCraft4U reserves the right to:' },
          { kind: 'list', items: [
            'Issue formal warnings regarding minor policy infractions.',
            'Temporarily suspend course listings or instructor access.',
            'Permanently remove instructors and their content for severe or repeated violations.',
          ] },
        ],
      },
    ],
  },
}

/* ------------------------------------------------------------------ */
/*  Host photo lookup. Live events carry a host name but often no        */
/*  profile picture from the backend, so we match the name against our    */
/*  real roster portraits (instructors, consult crafters, mentors) and    */
/*  show the right face. Callers fall back to a generic portrait on miss. */
/* ------------------------------------------------------------------ */
function hostNorm(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/\b(acharya|dr|pt|pandit|ustad|ustaad|shri|smt|mr|mrs|ms)\b\.?/g, '')
    .replace(/ji\b/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

let hostPhotoIndex: Record<string, string> | null = null
function buildHostPhotoIndex(): Record<string, string> {
  const map: Record<string, string> = {}
  const add = (name?: string, photo?: string) => {
    if (!name || !photo) return
    const n = hostNorm(name)
    if (!n) return
    if (!(n in map)) map[n] = photo
    const first = n.split(' ')[0]
    if (first && !(first in map)) map[first] = photo
  }
  for (const lane of [...zenInstructorLanes, ...musicInstructorLanes])
    for (const p of lane.people) add(p.name, p.photo)
  for (const c of cosmosConsultCrafters) {
    add(c.name, c.photo)
    add(c.displayName, c.photo)
  }
  for (const m of cosmosMentors) {
    add(m.name, m.photo)
    add(m.displayName, m.photo)
  }
  for (const page of Object.values(communityPages))
    for (const m of page.people?.mentors ?? []) add(m.name, m.photo)
  return map
}

/** Real roster portrait for an event host, matched loosely by name
 *  (honorific-aware, first-name fallback). Undefined when no match. */
export function hostPhotoFor(name?: string): string | undefined {
  if (!name) return undefined
  if (!hostPhotoIndex) hostPhotoIndex = buildHostPhotoIndex()
  const n = hostNorm(name)
  if (!n) return undefined
  return hostPhotoIndex[n] ?? hostPhotoIndex[n.split(' ')[0]]
}
